import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

const TYPING_CLIENT_PREFIX = "frontier.threadTyping.v1"
const TYPING_HEARTBEAT_MS = 2_000
const TYPING_IDLE_MS = 4_000
const TYPING_LEAVE_GRACE_MS = 800
const DRAFT_PERSIST_MS = 1_000

const pendingTypingLeaves = new Map()

function typingLeaveKey(conversationId, clientId) {
  return `${conversationId}:${clientId}`
}

function cancelPendingTypingLeave(conversationId, clientId) {
  const key = typingLeaveKey(conversationId, clientId)
  const handle = pendingTypingLeaves.get(key)
  if (!handle) return

  window.clearTimeout(handle)
  pendingTypingLeaves.delete(key)
}

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content
}

function sendTypingLeave({ url, clientId }) {
  const target = `${url}?client_id=${encodeURIComponent(clientId)}`
  fetch(target, {
    method: "DELETE",
    headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
    keepalive: true
  }).catch(() => {})
}

function schedulePendingTypingLeave({ conversationId, clientId, url, delay }) {
  cancelPendingTypingLeave(conversationId, clientId)
  const key = typingLeaveKey(conversationId, clientId)
  pendingTypingLeaves.set(key, window.setTimeout(() => {
    pendingTypingLeaves.delete(key)
    sendTypingLeave({ url, clientId })
  }, delay))
}

// The composer: a rich-text editor, the files picked for this message, a quiet format strip,
// and a footer of insert tools plus Send — all inside one box.
//
// Reply and Comment are two forms on the thread, each locked to one audience. One controller
// still serves both (and the in-card comment edit) because every behavior here — editor
// mirroring, the toolbar, paste sanitizing, upload staging, the in-flight state, the collapse,
// the tall expand, drafts — is identical either way. What differs is where Send posts, and that
// is the form's own action. Reply starts collapsed; open() is what the latest-message control
// calls.
//
// Sending really does reach Gmail and blocks for about a second, so the in-flight state is
// shown rather than left to look like a dead button. Files are uploaded the moment they are
// picked, which is what lets a rejected send hand them straight back instead of losing them.
//
// It also drives the form that corrects a comment in place (see comments/_edit), which renders the
// same partials minus the attachment picker and the sign-off — so the parts of
// this that touch those ask whether their target is there. That is the whole cost of the two
// surfaces sharing one editor, and it buys the alternative not existing: a second lighter editor
// would be a second answer to paste sanitizing, mention insertion and the sanitizer they imply.
export default class extends Controller {
  static targets = [
    "editor", "bodyField", "submit", "status", "hint", "format", "linkButton", "linkRow",
    "linkInput", "linkError", "files", "filesList", "filesTotal", "filesRejected",
    "fileTemplate", "fileInput", "attach", "dropzone", "signature", "expand",
    "mentions", "mentionsList", "mentionsEmpty", "mentionOption", "mentionChip",
    "mentionMailto",
    "emojiButton", "emojiPanel", "emojiGrid",
    "templatesButton", "templatesPanel", "templatesList", "templatesEmpty", "templateOption",
    "templateField", "macroNotice", "macroSummary",
    "knowledgeButton", "knowledgePanel", "knowledgeList", "knowledgeEmpty", "knowledgeOption",
    "knowledgeSearch", "knowledgeSearchWrap",
    "aiDraftButton", "aiDraftError", "grounding", "groundingLabel", "groundingList", "groundingItem",
    "recipients", "fromField", "toField", "ccField", "bccField", "bccToggle", "bccRow",
    "fromDisplay", "anchor", "anchorExcerpt", "messageField", "parentMessageField",
    "subjectField", "seenOutboundField", "confirmStaleSendField", "handoff"
  ]

  static values = {
    maxBytes: Number, directUploadUrl: String, mentionableUrl: String, autofocus: Boolean,
    conversationId: Number, userId: Number, replyTemplatesUrl: String,
    knowledgeArticlesUrl: String, typingUrl: String,
    draftUrl: String, handoffUrl: String, serverDrafts: Object, replyDraftUrl: String, aiDraftsMode: String,
    forwardsUrl: String, messagesUrl: String, recipientSuggestionsUrl: String,
    compose: Boolean
  }

  static DRAFT_PREFIX = "frontier.composerDraft.v1"

  // Only what the composer toolbar itself can produce, mirroring ReplyBodySanitizer on the
  // server. Anything pasted from outside is narrowed to this before it enters the editor.
  static PASTE_ALLOWED_TAGS = [ "p", "br", "strong", "b", "em", "i", "u", "a", "ol", "ul", "li" ]
  // Not allowed, and not dissolved either: a div is where a line starts. Unwrapping one joins two
  // lines of a pasted block into one, which is the same collapse ReplyBodySanitizer#break_lines_out_of_divs
  // exists to stop on the server — the two have to agree, or a paste reads one way in the box and
  // arrives another. A break rather than a paragraph, for the reason that method gives.
  static PASTE_LINE_TAGS = [ "div" ]
  static PASTE_DROPPED_TAGS = [ "script", "style" ]
  static PASTE_ALLOWED_SCHEMES = [ "http", "https", "mailto" ]
  // Explicit http(s) only. A bare "nooklyn.com" is what people type into the link field, not
  // what they paste into a sentence — treating every dotted word as a URL would link "e.g."
  // and "done.txt".
  static PASTE_URL_PATTERN = /https?:\/\/[^\s<>"'`]+/i

  connect() {
    this.alignEnterWithSoftReturns()
    this.nameShortcutModifier()
    this.handleSelectionChange = this.handleSelectionChange.bind(this)
    document.addEventListener("selectionchange", this.handleSelectionChange)
    this.rejectedNames = []
    // Applied on connect and not only on a switch, so the mode's affordances are read from the one
    // place that describes them rather than half from the server render and half from here.
    // Idempotent: the server rendered this same mode's copy, off the same table.
    this.applyMode(this.checkedMode)
    this.restoreDraft()
    this.reconcileServerDraft()
    this.syncSignatureTemplate()
    // Saving is skipped until this is set, so the empty editor on a fresh connect cannot wipe
    // the stash restoreDraft is about to read.
    this.draftReady = true
    if (this.pendingServerPush) {
      this.persistServerDraft({ immediate: true, mode: this.pendingServerPush })
      this.pendingServerPush = null
    }
    this.syncBody()
    this.refreshTotal()
    // Reply stays hidden until the latest-message control (or a restored draft) opens it.
    // Comment still rests as a single line. Only the send response asks for focus.
    if (this.isReplyMode) this.setOpen(this.autofocusValue || this.hasContent())
    if (this.isComposeMode) this.focusComposeStart()
    else if (this.autofocusValue) this.focusEditorEnd()
    this.syncExpanded()
    this.revealOpenReply()
    this.typingClientId = this.readTypingClientId()
    this.handleTypingVisibility = () => this.typingVisibilityChanged()
    this.handleTypingPageHide = () => this.typingPageHide()
    document.addEventListener("visibilitychange", this.handleTypingVisibility)
    window.addEventListener("pagehide", this.handleTypingPageHide)
    this.typingEnabled = true
  }

  disconnect() {
    document.removeEventListener("selectionchange", this.handleSelectionChange)
    // The caret timer moves the *document's* selection, so one left pending on a composer that
    // has been swapped out — every send replaces this element — would reach past its own detached
    // editor and clear the selection in the composer that replaced it.
    clearTimeout(this.caretTimer)
    this.closeMentions()
    this.closeReplyTemplates()
    this.closeKnowledge()
    if (!this.draftDiscarded) {
      this.saveDraft()
      this.persistServerDraft({ immediate: true, keepalive: true })
    }
    this.setTall(false)
    document.removeEventListener("visibilitychange", this.handleTypingVisibility)
    window.removeEventListener("pagehide", this.handleTypingPageHide)
    this.stopTyping({ delay: TYPING_LEAVE_GRACE_MS })
  }

  // The editor's selection is recorded on every change, not only on a toolbar mousedown —
  // activating the Link button from the keyboard fires click with no mousedown at all, and
  // without this the range would be stale by the time the URL field takes focus.
  handleSelectionChange() {
    this.saveSelection()
    this.refreshToolbar()
  }

  // maybeSubmit accepts either modifier, so the hint has to name whichever one the reader
  // actually has — and the key is labelled Return on a Mac keyboard but Enter on a PC one.
  // Rendered as the Mac wording server-side, since most of the team is on one, which keeps the
  // common case free of a visible swap.
  //
  // The verb comes from the markup because the same shortcut does different things: it sends from
  // the composer and saves from a note's edit form.
  nameShortcutModifier() {
    if (!this.hasHintTarget) return

    const platform = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent
    const isApple = /mac|iphone|ipad|ipod/i.test(platform)
    const verb = this.hintTarget.dataset.shortcutVerb
    this.hintTarget.textContent = isApple ? `⌘ + Return to ${verb}` : `Ctrl + Enter to ${verb}`
  }

  // A failed send re-renders the composer with the markup preserved; dropping the caret at the
  // start would put it in front of everything they already wrote.
  focusEditorEnd() {
    const range = document.createRange()
    range.selectNodeContents(this.editorTarget)
    range.collapse(false)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
    this.editorTarget.focus()
  }

  // ---- body ----

  // A contenteditable is not a form control and submits nothing of its own, so the hidden field
  // is the only thing the server ever sees.
  syncBody() {
    this.normalizeEmptyEditor()
    this.bodyFieldTarget.value = this.editorTarget.innerHTML
    // An emptied box is a reply the agent gave up on, and the macro they armed went with it — the
    // only cheap, honest signal that the words the macro came with are no longer here. Anything
    // finer (tracking whether the inserted markup survived an edit) would be guessing at intent.
    if (!this.hasBodyText()) this.clearMacro()
    this.update()
    this.refreshMentions()
    this.refreshReplyTemplates()
  }

  // Browsers leave a stray <br> or an empty <div> behind once you type and delete, and the
  // placeholder is drawn from :empty — without this it disappears the first time the box is
  // used and never comes back.
  normalizeEmptyEditor() {
    if (this.editorText !== "") return
    // A list the agent has just started carries no text yet but is absolutely not empty;
    // clearing it here would delete the thing they only just asked for. A mention chip with
    // its @name is the same: the chip is the note.
    if (this.editorTarget.querySelector("ol, ul, a, .comment-mention")) return

    this.editorTarget.innerHTML = ""
  }

  get editorText() {
    return this.editorTarget.textContent.replace(/ /g, " ").trim()
  }

  // The sign-off is put in the box for the agent rather than written by them, so on its own it is
  // not a reply: it must neither arm Send nor hold the composer open the way typed text does.
  hasBodyText() {
    if (this.editorText === "") return false

    return this.editorText !== this.signatureText
  }

  update() {
    this.syncSendButton()
    this.syncHandoffVisibility()
    this.syncExpanded()
    this.saveDraft()
    this.syncTyping()
  }

  // Send starts disabled. The reason has to live on the button and in the hint — a muted
  // control with cursor:not-allowed and title "Send" is what an agent reports as "no
  // permission". Recipients, subject, uploads and emptiness each get their own sentence.
  syncSendButton() {
    if (!this.hasSubmitTarget) return

    const ready = this.canSend()
    this.submitTarget.disabled = !ready
    if (!this.submitTarget.dataset.sendLabel) return

    const label = ready ? this.sendReadyLabel() : this.sendBlockReason()
    this.submitTarget.title = label
    this.submitTarget.setAttribute("aria-label", label)
    this.syncSendHint(ready, label)
  }

  sendReadyLabel() {
    return this.submitTarget.dataset.sendLabel || "Send"
  }

  sendBlockReason() {
    if (this.uploadingRows.length > 0) return "Wait for files to finish uploading"
    if (this.totalBytes > this.maxBytesValue) return "Those files are too large to send"
    if (this.isComposeMode && !this.hasToRecipient()) return "Add a recipient"
    if (this.isComposeMode && !this.hasSubject()) return "Add a subject"
    if (this.hasBodyText() || this.readyRows.length > 0) return this.sendReadyLabel()

    return "Write a message or attach a file"
  }

  syncSendHint(ready, label) {
    if (!this.hasHintTarget || !this.isComposeMode) return
    if (ready) {
      this.nameShortcutModifier()
      return
    }

    this.hintTarget.textContent = label
  }

  syncHandoffVisibility() {
    if (!this.hasHandoffTarget) return

    this.handoffTarget.hidden = !this.draftable()
  }

  // ---- mode ----

  // Locked to the form's data-mode. Reply and Comment are separate forms, so there is
  // no radio left to read.
  get checkedMode() {
    return this.lockedMode
  }

  get lockedMode() {
    const mode = this.element.dataset.mode
    if (mode !== "reply" && mode !== "comment" && mode !== "compose") return null

    // Same attributes the mode radios used to carry, now stamped on the form
    // itself so applyMode can read them without a switch.
    return {
      mode,
      url: this.element.getAttribute("action"),
      placeholder: this.hasEditorTarget ? this.editorTarget.dataset.placeholder : "",
      editorLabel: this.hasEditorTarget ? this.editorTarget.getAttribute("aria-label") : "",
      sendLabel: this.hasSubmitTarget ? (this.submitTarget.dataset.sendLabel || this.submitTarget.getAttribute("aria-label")) : "",
      signature: this.element.dataset.signature || (this.hasSignatureTarget ? "true" : "false"),
      filesNoun: this.element.dataset.filesNoun || (mode === "comment" ? "comment" : "reply")
    }
  }

  // Palette and picker options sit above the box. Letting mousedown take focus off the
  // editor collapses the composer and can slide the target out from under the click.
  holdPosition(event) {
    event.preventDefault()
  }

  // Reply starts hidden. The latest-message control calls this; a restored draft does too.
  // An empty editor is focused rather than collapsed-to-end: signOff parks the caret on
  // the blank line between greeting and sign-off, and focusEditorEnd would sit past both.
  open(options = {}) {
    if (!this.isReplyMode) return

    if (!options.keepCompose) this.resetComposeTarget()
    if (!this.replyIsOpen) this.stampSeenOutbound()

    this.setOpen(true)
    this.expand()
    if (this.hasBodyText()) this.focusEditorEnd()
    else this.editorTarget.focus()
    this.revealOpenReply()
  }

  // Collapse without sending. The draft stays so Reply can pick it up again.
  close() {
    if (!this.isReplyMode) return

    this.saveDraft()
    this.persistServerDraft({ immediate: true })
    this.hideReply()
  }

  // Copy this composer onto a teammate's own row. Flush first so the server
  // has the latest words; never deleteServerDraft after — that would wipe the
  // parked undo copy. Forget local before applying the stream so disconnect
  // cannot persist or empty-delete what we just passed.
  async passDraft(event) {
    const userId = event.params.userId
    if (!userId || this.passingDraft) return
    if (!this.draftable()) return
    if (!this.hasHandoffUrlValue || !this.handoffUrlValue) return

    this.passingDraft = true
    this.setHandoffBusy(true)
    this.closeHandoffMenu()
    this.syncBody()
    this.saveDraft()

    try {
      await this.persistServerDraft({ immediate: true })
      const response = await fetch(this.handoffUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken()
        },
        body: JSON.stringify({
          user_id: userId,
          mode: this.element.dataset.mode
        }),
        credentials: "same-origin"
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        this.statusTarget.textContent = data.error || "Could not pass this draft."
        return
      }

      const html = await response.text()
      this.draftDiscarded = true
      this.abortServerDraft()
      this.forgetDraft()
      this.rememberServerDraft(this.element.dataset.mode, null)
      window.Turbo.renderStreamMessage(html)
      this.clearComposer()
      this.clearMacro()
      this.stopTyping({ immediate: true })
      if (this.isReplyMode) this.hideReply()
    } catch {
      this.statusTarget.textContent = "Could not pass this draft."
    } finally {
      this.passingDraft = false
      this.draftDiscarded = false
      this.setHandoffBusy(false)
    }
  }

  closeHandoffMenu() {
    if (!this.hasHandoffTarget) return

    this.application.getControllerForElementAndIdentifier(this.handoffTarget, "typeahead")?.close()
  }

  setHandoffBusy(busy) {
    if (!this.hasHandoffTarget) return

    const trigger = this.handoffTarget.querySelector("[aria-haspopup]")
    if (trigger) trigger.disabled = busy
  }

  // Throw the draft away and hide Reply. Cancel (close) keeps the stash; this
  // is the other half of that pair. Confirm only when there is something to lose.
  discard() {
    if (!this.isReplyMode && !this.isComposeMode) return
    if (!this.draftable()) {
      if (this.isComposeMode) return
      this.close()
      return
    }
    if (!window.confirm("Discard this draft?")) return

    this.draftDiscarded = true
    this.aiDraftCancelled = true
    this.aiDraftRequested = true
    this.abortServerDraft()
    this.forgetDraft()
    this.deleteServerDraft(this.element.dataset.mode, { keepalive: true })
    this.clearComposer()
    this.clearMacro()
    this.clearAiDraftError()
    this.clearGrounding()
    this.stopTyping({ immediate: true })
    this.draftDiscarded = false
    if (this.isComposeMode) {
      this.update()
      return
    }
    this.hideReply()
  }

  hideReply() {
    this.closeEmoji()
    this.closeReplyTemplates()
    this.closeKnowledge()
    this.closeMentions()
    this.setTall(false)
    this.setOpen(false)
  }

  setOpen(open) {
    const root = this.element.closest("#reply_composer")
    if (!root) return

    root.hidden = !open
    root.classList.toggle("is-open", open)
  }

  get replyIsOpen() {
    const root = this.element.closest("#reply_composer")
    return Boolean(root) && !root.hidden
  }

  // After expand/focus the draft card has its real height. Scroll then, not in
  // setOpen — otherwise the footer growing leaves Send below the fold.
  revealOpenReply() {
    const root = this.element.closest("#reply_composer")
    if (!root || root.hidden) return

    const scroller = root.closest(".thread__scroll")
    if (!scroller) return

    scroller.scrollTop = scroller.scrollHeight
  }

  // Where the audience actually changes hands. The form's action is what a submit posts to, so it
  // moves first and in the same turn as the copy the writer is reading — the two must never
  // disagree about who is about to receive this.
  applyMode(mode) {
    if (!mode) return

    if (mode.url) this.element.action = mode.url
    // Read by the stylesheet, which recolors the whole surface off it. A note to the team must not
    // look like mail on its way to a client.
    this.element.dataset.mode = mode.mode
    this.editorTarget.dataset.placeholder = mode.placeholder
    this.editorTarget.setAttribute("aria-label", mode.editorLabel)
    this.submitTarget.setAttribute("aria-label", mode.sendLabel)
    this.signaturePermitted = mode.signature === "true"
    // Only the wording of an over-cap rejection differs by mode; the cap and the staging behind it
    // are shared, so this is a noun rather than a capability.
    this.filesNoun = mode.filesNoun
    if (mode.mode !== "comment") {
      this.unwrapMentionChips()
      this.syncBody()
    }
    this.closeMentions()
    this.closeEmoji()
    this.closeReplyTemplates()
    this.closeKnowledge()
    // The audience changed, so the reply the macro was picked for is not the thing in the box any
    // more — and a note to your own team has no thread actions to carry. Cleared here rather than
    // left to syncBody's emptiness rule, which a restored draft would put straight back over.
    this.clearMacro()
    this.syncTemplatesButton()
    this.syncKnowledgeButton()
    this.syncAiDraftButton()
    this.syncRecipients()
    // A note's anchor has no meaning on a reply. Cleared after the comment draft
    // was already stashed, so switching back restores the chip with the words.
    if (mode.mode !== "comment") this.setAnchor("", "")
  }

  showBcc() {
    if (!this.hasBccRowTarget) return

    this.bccRowTarget.hidden = false
    if (this.hasBccToggleTarget) {
      this.bccToggleTarget.hidden = true
      this.bccToggleTarget.setAttribute("aria-expanded", "true")
    }
    this.focusRecipientInput(this.hasBccFieldTarget ? this.bccFieldTarget : null)
  }

  syncRecipients() {
    if (!this.hasRecipientsTarget) return

    const hidden = this.isCommentMode
    this.recipientsTarget.hidden = hidden
    this.recipientFields.forEach((field) => { field.disabled = hidden })
    this.recipientsTarget.querySelectorAll("[data-recipient-chips-target='input']").forEach((input) => {
      input.disabled = hidden
    })
  }

  // Chrome already wraps Return in a div. Firefox wraps it in a p, and every reader of
  // the sanitizer's eleven tags puts a blank line between two of those — so one Enter
  // in Firefox would leave as a paragraph gap. The sanitizer turns the divs into br;
  // this is what makes the key mean the same thing in both browsers.
  alignEnterWithSoftReturns() {
    document.execCommand("defaultParagraphSeparator", false, "div")
  }

  // ---- signature ----

  // Read back off the template rather than repeated as a string here, so the emptiness rule above
  // cannot drift away from the markup that is actually inserted.
  get signatureText() {
    if (!this.hasSignatureTarget) return ""

    return this.signatureTarget.content.textContent.replace(/ /g, " ").trim()
  }

  // The agent gets one sign-off per composer, on their way into the editor. Deleting it is an
  // edit like any other, so it is never put back — a fresh composer, after a send, starts over.
  signOff() {
    // Before the flag below, not after it: a comment must leave no trace of having declined a
    // sign-off, so switching to Reply afterwards still gets one.
    if (!this.signaturePermitted) {
      this.maybeAutoDraft()
      return
    }
    if (this.signedOff) return
    // Set before the guard below, not after it: a composer re-rendered around a rejected send
    // already holds a sign-off, and leaving the flag clear would let a later focus add a second.
    this.signedOff = true
    if (!this.hasSignatureTarget) {
      this.maybeAutoDraft()
      return
    }
    if (this.editorText !== "") {
      this.maybeAutoDraft()
      return
    }

    this.editorTarget.appendChild(this.signatureTarget.content.cloneNode(true))
    this.parkCaretInBlankLine()
    // The editor posts nothing of its own, so without this the agent could send a reply whose
    // sign-off never reached the hidden field — and update() is what arms Send and expands the box.
    this.syncBody()
    this.maybeAutoDraft()
  }

  // The template is rendered for the thread's own mailbox. A restored draft, or a From
  // switch, can point at a different one — keep the clone-source in step so the first
  // signOff (and hasBodyText) describe the mailbox that will actually send.
  syncSignatureTemplate() {
    if (!this.hasSignatureTarget) return

    const source = this.mailboxSignoff()
    if (!source) return

    const templateSignoff = this.signatureTarget.content.querySelector("[data-composer-signoff]")
    if (!templateSignoff) return

    templateSignoff.replaceWith(this.signoffElement(source.innerHTML))
  }

  changeFrom() {
    this.applyMailboxSignature()
    this.updateComposeSuggestionUrls()
    this.saveDraft()
  }

  updateComposeSuggestionUrls() {
    if (!this.isComposeMode || !this.hasRecipientSuggestionsUrlValue || !this.hasFromFieldTarget) return

    const mailboxId = this.fromFieldTarget.value
    if (!mailboxId) return

    const url = `${this.recipientSuggestionsUrlValue}?mailbox_id=${encodeURIComponent(mailboxId)}`
    this.element.querySelectorAll("[data-controller~='recipient-chips']").forEach((element) => {
      const controller = this.application.getControllerForElementAndIdentifier(element, "recipient-chips")
      if (controller) controller.suggestionsUrlValue = url
    })
  }

  applyMailboxSignature() {
    if (!this.hasSignatureTarget) return

    const source = this.mailboxSignoff()
    if (!source) return

    const next = this.signoffElement(source.innerHTML)
    const templateSignoff = this.signatureTarget.content.querySelector("[data-composer-signoff]")
    if (templateSignoff) templateSignoff.replaceWith(next.cloneNode(true))

    const editorSignoff = this.editorTarget.querySelector("[data-composer-signoff]")
    if (editorSignoff) editorSignoff.replaceWith(next)

    this.updateFromDisplay(source)
    this.syncBody()
  }

  mailboxSignoff() {
    if (!this.hasFromFieldTarget) return null

    const mailboxId = this.fromFieldTarget.value
    if (!mailboxId) return null

    return this.element.querySelector(`template[data-composer-signoff-for="${CSS.escape(mailboxId)}"]`)
  }

  signoffElement(html) {
    const wrap = document.createElement("div")
    wrap.setAttribute("data-composer-signoff", "")
    wrap.append(this.sanitizedFragment(html))
    return wrap
  }

  updateFromDisplay(source) {
    if (!this.hasFromDisplayTarget) return

    const fromName = source.dataset.fromName
    const email = source.dataset.email
    if (!fromName || !email) return

    this.fromDisplayTarget.textContent = `${fromName} <${email}>`
  }

  // The caret belongs in the blank line the skeleton leaves for the reply.
  //
  // Deferred by a turn rather than set inline, because a click is what usually brings us here and
  // the browser places its own caret as the *default action* of that click — after our focus
  // handler has run. Setting the range synchronously would simply be overwritten, and since the
  // agent clicked an empty editor, the position they hit-tested carries no intent about content
  // that did not exist yet. A programmatic focus (the composer that comes back after a send) has
  // no default action to lose to and lands here just the same, one turn later.
  parkCaretInBlankLine() {
    this.caretTimer = setTimeout(() => {
      // Found by content rather than by position, because the blank line moves: it is the first
      // block when the skeleton is only a sign-off, and the second when a greeting opens it.
      const blank = Array.from(this.editorTarget.children)
        .find((block) => block.textContent.trim() === "")
      const target = blank || this.editorTarget.firstChild
      if (!target) return

      const range = document.createRange()
      range.setStart(target, 0)
      range.collapse(true)
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
    }, 0)
  }

  // ---- resting / expanded ----

  // At rest the composer is one line. Everything that makes it a rich-text composer — the
  // toolbar, the action row, the shortcut hint — is collapsed until the agent is actually
  // composing, which is either while they are in it or while it is holding something.

  expand() {
    this.element.classList.add("is-expanded")
  }

  // Restyled in place, never re-rendered: replacing a contenteditable drops its content, its
  // caret, and any DirectUpload still in flight. The thread class is what lets the messages
  // column yield its height; the editor node itself does not move.
  toggleTall() {
    this.setTall(!this.isTall)
  }

  get isTall() {
    return this.element.classList.contains("is-tall")
  }

  setTall(tall) {
    this.element.classList.toggle("is-tall", tall)
    this.element.closest(".thread")?.classList.toggle("is-composer-tall", tall)
    if (this.hasExpandTarget) {
      const label = tall ? "Collapse composer" : "Expand composer"
      this.expandTarget.setAttribute("aria-expanded", String(tall))
      this.expandTarget.setAttribute("aria-label", label)
      this.expandTarget.setAttribute("title", label)
    }
    if (tall) this.expand()
    else this.syncExpanded()
    // Growing this pane is a large layout shift. If the document is even a few
    // pixels taller than the viewport, the browser scrolls it to keep the
    // focused editor on screen — and the inbox shell is no longer the scrollport.
    this.lockDocumentScroll()
  }

  lockDocumentScroll() {
    window.scrollTo(0, 0)
    requestAnimationFrame(() => window.scrollTo(0, 0))
  }

  // relatedTarget is whatever is about to take focus; a null one means focus went somewhere we
  // cannot name — which includes the native file dialog the (+) button opens and an ordinary
  // tab switch, neither of which is the agent leaving the composer. document.hasFocus() is what
  // tells those apart from a real click elsewhere on the page.
  focusOut(event) {
    if (this.element.contains(event.relatedTarget)) return
    if (!document.hasFocus()) return

    // A palette left open above a composer the agent has walked away from is the composer's own
    // chrome outliving the composing — and it would sit there while everything around it collapsed.
    this.closeEmoji()
    this.closeReplyTemplates()
    this.closeKnowledge()
    this.syncExpanded()
  }

  syncExpanded() {
    this.element.classList.toggle("is-expanded", this.shouldExpand())
  }

  shouldExpand() {
    if (this.isTall) return true
    if (this.element.contains(document.activeElement)) return true
    // Collapsing mid-send would take the spinner and the disabled Send button off screen.
    if (this.element.classList.contains("is-sending")) return true

    return this.hasContent()
  }

  // Deliberately the same emptiness rule canSend() uses, plus three things that are not text:
  // a list or link the agent has only just started — which normalizeEmptyEditor keeps on purpose
  // — staged files, whose chips and running total exist nowhere else on the page, and an unread
  // over-cap rejection. That last one is the odd member: the composer is genuinely empty, but
  // collapsing would swallow the only explanation the agent gets for a file that has silently
  // vanished from their reply. It clears on their next pick, which is when the notice is stale.
  hasContent() {
    if (this.hasBodyText()) return true
    if (this.editorTarget.querySelector("ol, ul, a, .comment-mention")) return true
    if (this.fileRows.length > 0) return true

    return this.hasFilesRejectedTarget && !this.filesRejectedTarget.hidden
  }

  // A reply carrying only files is legitimate, so text is not required — but nothing may be
  // sent while an upload is still in flight or the payload is over the cap.
  canSend() {
    if (this.uploadingRows.length > 0) return false
    if (this.totalBytes > this.maxBytesValue) return false
    if (this.isComposeMode && !this.composeReady()) return false

    return this.hasBodyText() || this.readyRows.length > 0
  }

  composeReady() {
    return this.hasToRecipient() && this.hasSubject()
  }

  hasToRecipient() {
    return this.hasToFieldTarget && this.toFieldTarget.value.trim() !== ""
  }

  hasSubject() {
    return this.hasSubjectFieldTarget && this.subjectFieldTarget.value.trim() !== ""
  }

  focusComposeStart() {
    if (this.hasToFieldTarget && !this.toFieldTarget.value.trim()) {
      this.focusRecipientInput(this.toFieldTarget)
      return
    }
    if (this.hasSubjectFieldTarget && !this.subjectFieldTarget.value.trim()) {
      this.subjectFieldTarget.focus()
      return
    }
    this.focusEditorEnd()
  }

  // Escape means four different things in this editor, so the order it is offered around in is fixed
  // and explicit rather than left to whichever listener happens to fire: dismiss the typeahead that
  // is open, then collapse a tall composer, then nothing. A panel added by listening for Escape
  // wherever it liked would collapse the composer out from under its own dismissal.
  //
  // Mentions and templates can both live on Reply now (`@` vs `/`), but only one query is
  // live at a caret, so their order relative to each other decides nothing; it is their
  // position in front of the collapse that does.
  editorKeydown(event) {
    if (this.handleMentionKey(event)) return
    if (this.handleReplyTemplateKey(event)) return
    if (this.handleKnowledgeKey(event)) return
    if (this.collapseTallOnEscape(event)) return
    this.maybeSubmit(event)
  }

  // Reply, compose, and the template editor are mail: one Return is one line, the way Gmail
  // does it. The browser's default is a new block, and those stack like paragraphs — which is
  // what made the box look airier than the card. Comment keeps that block rhythm on purpose.
  //
  // Lists are the exception: Return inside an item has to make another item, not a break
  // inside the current one. Mentions / templates / ⌘Enter already preventDefault on keydown
  // so they never reach insertParagraph.
  editorBeforeInput(event) {
    if (event.inputType !== "insertParagraph") return
    if (!this.usesSoftEnter) return
    if (this.caretInsideList()) return

    event.preventDefault()
    document.execCommand("insertLineBreak")
    this.syncBody()
  }

  get usesSoftEnter() {
    return !this.isCommentMode
  }

  caretInsideList() {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return false

    const node = selection.anchorNode
    if (!node || !this.editorTarget.contains(node)) return false

    const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement
    const item = element?.closest("li")
    return Boolean(item && this.editorTarget.contains(item))
  }

  collapseTallOnEscape(event) {
    if (event.key !== "Escape") return false
    if (!this.isTall) return false

    event.preventDefault()
    this.setTall(false)
    this.editorTarget.focus()
    return true
  }

  maybeSubmit(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault()
      if (!this.submitTarget.disabled) this.element.requestSubmit()
    }
  }

  // ---- formatting ----

  // Fired on mousedown, before the click. Without stopping the default the button takes focus
  // and the editor's selection collapses, so the command would land on nothing.
  holdSelection(event) {
    event.preventDefault()
    this.saveSelection()
  }

  format(event) {
    const command = event.currentTarget.dataset.command
    this.editorTarget.focus()
    this.ensureEditableBlock()
    document.execCommand(command, false, null)
    this.syncBody()
    this.refreshToolbar()
  }

  // insertOrderedList / insertUnorderedList need a block to convert, and a genuinely empty
  // contenteditable has none — the command silently does nothing. Starting a list on an empty
  // composer is an ordinary thing to do, so give it a paragraph to work with and put the caret
  // inside it.
  ensureEditableBlock() {
    if (this.editorTarget.innerHTML !== "") return

    this.editorTarget.innerHTML = "<p><br></p>"
    const range = document.createRange()
    range.setStart(this.editorTarget.firstElementChild, 0)
    range.collapse(true)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
  }

  // Driven by selectionchange rather than by clicks, so moving the caret into bold text lights
  // the button too — the state belongs to where the caret is, not to what was last pressed.
  refreshToolbar() {
    if (!this.hasFormatTarget) return
    if (!this.editorHasFocus) return

    this.formatTargets.forEach((button) => {
      button.setAttribute("aria-pressed", String(this.commandActive(button.dataset.command)))
    })
  }

  // queryCommandState is deprecated along with execCommand and throws on some commands in some
  // engines. The pressed state is a nicety; losing it must never break typing.
  commandActive(command) {
    try {
      return document.queryCommandState(command)
    } catch {
      return false
    }
  }

  // ---- links ----

  toggleLink() {
    if (this.linkRowTarget.hidden) this.openLink()
    else this.cancelLink()
  }

  openLink() {
    this.closeKnowledge()
    // Captured here, from the live selection, rather than trusting whatever the last
    // selectionchange happened to record — that event is asynchronous, so on a keyboard
    // activation it may not have been delivered yet. At this instant nothing has taken focus
    // from the editor yet, so this is the last moment the real selection is readable.
    this.saveSelection()
    this.linkRowTarget.hidden = false
    this.linkButtonTarget.setAttribute("aria-expanded", "true")
    this.linkErrorTarget.hidden = true
    // Focusing the field is what destroys the editor's selection, so the range saved on
    // mousedown is the only record of what the agent had highlighted.
    this.linkInputTarget.value = this.currentLinkHref()
    this.linkInputTarget.focus()
    this.linkInputTarget.select()
  }

  // Editing a link the caret is already sitting in, rather than making a second one on top.
  currentLinkHref() {
    const anchor = this.savedRange?.commonAncestorContainer?.parentElement?.closest?.("a")

    return anchor?.getAttribute("href") || ""
  }

  applyLink() {
    const url = this.normalizedUrl(this.linkInputTarget.value)
    if (url === null) {
      this.linkErrorTarget.hidden = false
      this.linkInputTarget.classList.add("composer__link-input--invalid")
      return
    }

    this.restoreSelection()
    document.execCommand("createLink", false, url)
    this.closeLink()
    this.syncBody()
  }

  cancelLink() {
    this.closeLink()
    this.restoreSelection()
  }

  closeLink() {
    this.linkRowTarget.hidden = true
    this.linkErrorTarget.hidden = true
    this.linkInputTarget.classList.remove("composer__link-input--invalid")
    this.linkInputTarget.value = ""
    this.linkButtonTarget.setAttribute("aria-expanded", "false")
  }

  linkKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.applyLink()
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.cancelLink()
    }
  }

  // A bare "example.com" is what people type, so it is completed rather than refused. An
  // explicit scheme we do not accept is refused outright — silently rewriting javascript: into
  // https://javascript: would be worse than saying no. The server sanitizes regardless; this is
  // only here so the agent finds out before they send.
  normalizedUrl(value) {
    const url = value.trim()
    if (url === "") return null
    if (/^https?:\/\//i.test(url)) return url
    if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return null

    return `https://${url}`
  }

  // ---- emoji ----

  // A curated palette, rendered by the server and shown in the flow above the editor. Every glyph
  // is plain Unicode text, so inserting one is a text insertion and nothing more: no markup, no
  // sanitizer to widen, no bytes to fetch or store.

  toggleEmoji() {
    if (this.emojiOpen) this.closeEmoji()
    else this.openEmoji()
  }

  get emojiOpen() {
    return this.hasEmojiPanelTarget && !this.emojiPanelTarget.hidden
  }

  // Focus moves into the palette on open, the way it moves into the URL field: it is what makes
  // the arrow keys, Enter and Escape work without a second Tab, and the caret it leaves behind is
  // held in savedRange until an emoji is picked.
  openEmoji() {
    if (!this.hasEmojiPanelTarget) return

    this.closeKnowledge()
    this.emojiPanelTarget.hidden = false
    this.emojiButtonTarget.setAttribute("aria-expanded", "true")
    this.focusEmoji(0)
  }

  closeEmoji() {
    if (!this.emojiOpen) return

    // Focus sits inside the panel whenever it was opened from here rather than dismissed from
    // outside, and hiding the element that holds focus drops it on the body — which reads as the
    // whole composer collapsing. The editor is where the writing was going to continue anyway.
    const returnFocus = this.emojiPanelTarget.contains(document.activeElement)
    this.emojiPanelTarget.hidden = true
    this.emojiButtonTarget.setAttribute("aria-expanded", "false")
    if (returnFocus) this.editorTarget.focus()
  }

  pickEmoji(event) {
    const glyph = event.currentTarget.dataset.emoji
    // Inserted before the panel is hidden, so the editor has taken focus back by the time the
    // element holding it disappears.
    if (this.savedRange) this.insertEmoji(glyph)
    else this.insertEmojiOnceCaretIsPlaced(glyph)
    this.closeEmoji()
  }

  insertEmoji(glyph) {
    this.restoreSelection()
    // execCommand rather than a text node of our own, deprecated as it is: an insertion the
    // browser performed is one its undo stack knows about, so ⌘Z takes the glyph back out the way
    // it takes back a typed character.
    document.execCommand("insertText", false, glyph)
    this.syncBody()
  }

  // There is no caret to insert at when the agent reached the toolbar without ever entering the
  // editor. Focusing it is what places one — and in reply mode it is also what clones the sign-off
  // and parks the caret in the blank line left for the reply, which happens a turn later. Waiting
  // for that turn is what lands the glyph where the agent will type rather than in front of the
  // greeting. Registered after parkCaretInBlankLine's own timer and with the same delay, so it
  // runs after it.
  insertEmojiOnceCaretIsPlaced(glyph) {
    this.focusEditorEnd()
    setTimeout(() => this.insertEmoji(glyph), 0)
  }

  emojiKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.closeEmoji()
      return
    }

    const index = this.nextEmojiIndex(event)
    if (index === null) return

    event.preventDefault()
    this.focusEmoji(index)
  }

  // Null for a key the palette has no answer for, so the caller leaves it to the browser.
  // Wrapped rather than clamped, as the toolbar's own arrow keys are: the palette is a ring, and
  // a press that does nothing reads as a dead key.
  nextEmojiIndex(event) {
    const buttons = this.emojiButtons
    if (buttons.length === 0) return null
    if (event.key === "Home") return 0
    if (event.key === "End") return buttons.length - 1

    const columns = this.emojiColumns(buttons)
    const steps = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns }
    if (!(event.key in steps)) return null

    const current = buttons.indexOf(event.currentTarget)
    return (current + steps[event.key] + buttons.length) % buttons.length
  }

  // Measured off the layout rather than restated here: the grid is auto-filled, so how many glyphs
  // sit on a row is the stylesheet's answer and it changes with the width of the pane.
  emojiColumns(buttons) {
    const firstRow = buttons[0].offsetTop

    return buttons.filter((button) => button.offsetTop === firstRow).length || 1
  }

  // One tab stop for the whole palette, moved to whichever glyph is current — fifty-six stops
  // between the toolbar and Send would be worse than no keyboard support at all.
  focusEmoji(index) {
    const buttons = this.emojiButtons
    const target = buttons[index]
    if (!target) return

    buttons.forEach((button) => button.setAttribute("tabindex", "-1"))
    target.setAttribute("tabindex", "0")
    target.focus()
  }

  get emojiButtons() {
    if (!this.hasEmojiGridTarget) return []

    return Array.from(this.emojiGridTarget.querySelectorAll(".composer__emoji-option"))
  }

  // ---- reply templates ----

  // A reply the agent wrote once in Settings, put into this thread with its variables already filled
  // in. The bodies arrive from the server resolved against this conversation, so nothing here knows
  // the {{token}} grammar — the same division the mention list keeps, where the browser is handed
  // finished names rather than users to render.
  //
  // ONE panel, two ways in, and `templatesQuery` is which one is showing. Null means the toolbar
  // button opened it: every template is listed and focus has moved into the panel, so the arrows and
  // Enter are answered by templatesKeydown on the option holding focus. Set means a typed `/` opened
  // it: the list is filtered, the caret has not left the editor, and the keys are answered by
  // handleReplyTemplateKey with the active option announced through aria-activedescendant. The two
  // list the same templates and insert them the same way; only the query and where focus sits differ.

  // Reply mode only, and expressed as a getter beside isCommentMode rather than as an entry in
  // COMPOSER_MODES: that table's own header says it holds values and not capabilities, and the last
  // capability flag in it was deliberately removed. Every variable a template can carry names the
  // client or the thread, so there is nothing here for a note to your own team.
  get isReplyMode() {
    return this.element.dataset.mode === "reply"
  }

  get isComposeMode() {
    return this.element.dataset.mode === "compose" || this.composeValue
  }

  // Hidden rather than absent on the surfaces where it has no meaning, because the mode changes
  // client-side with no round trip to render a different toolbar — and `hidden` takes the button out
  // of the tab order and the accessibility tree at once. The panel's absence is what rules out the
  // form in Settings that WRITES templates: offering a picker inside that editor is a loop.
  syncTemplatesButton() {
    if (!this.hasTemplatesButtonTarget) return

    const available = this.hasTemplatesPanelTarget && this.isReplyMode
    this.templatesButtonTarget.hidden = !available
    if (!available) this.closeReplyTemplates()
  }

  // Same hide-rather-than-absent posture as the templates button: reply mode can
  // appear without a round trip, and `hidden` drops the control from the tab
  // order. The panel's absence is what rules out comment and the Settings form
  // that writes templates.
  syncKnowledgeButton() {
    if (!this.hasKnowledgeButtonTarget) return

    const available = this.hasKnowledgePanelTarget && this.isReplyMode
    this.knowledgeButtonTarget.hidden = !available
    if (!available) this.closeKnowledge()
  }

  // Same hide-rather-than-absent posture as the templates button: reply mode can
  // appear without a round trip, and `hidden` drops the control from the tab
  // order. The URL is what the server uses to say this project drafts at all.
  syncAiDraftButton() {
    if (!this.hasAiDraftButtonTarget) return

    this.aiDraftButtonTarget.hidden = !this.aiDraftsAvailable
  }

  get aiDraftsAvailable() {
    return this.hasReplyDraftUrlValue && this.replyDraftUrlValue !== "" && this.isReplyMode
  }

  draftWithAi(event) {
    event.preventDefault()
    this.requestAiDraft({ auto: false })
  }

  // Once per composer. A later empty editor (Cancel, delete) must not spend
  // another call — the button is still there for that.
  maybeAutoDraft() {
    if (this.aiDraftRequested) return
    if (this.aiDraftsModeValue !== "always") return
    if (!this.aiDraftsAvailable) return
    if (this.hasBodyText()) return

    this.aiDraftRequested = true
    this.requestAiDraft({ auto: true })
  }

  requestAiDraft({ auto = false } = {}) {
    if (!this.aiDraftsAvailable || this.aiDraftInFlight) return

    this.aiDraftCancelled = false
    this.aiDraftInFlight = true
    const snapshot = this.editorTarget.innerHTML
    this.clearAiDraftError()
    this.setDrafting(true)

    fetch(this.replyDraftUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      credentials: "same-origin"
    }).then((response) => response.json().then((data) => ({ response, data }), () => ({ response, data: {} })))
      .then(({ response, data }) => {
        if (this.aiDraftCancelled) return
        if (!response.ok) {
          this.showAiDraftError(data.error || "Couldn't draft a reply. Try again.")
          return
        }
        if (!data.body_html) {
          this.showAiDraftError("Couldn't draft a reply. Try again.")
          return
        }
        if (auto && this.hasBodyText()) return
        if (!auto && this.editorTarget.innerHTML !== snapshot) return

        this.writeAiDraft(data.body_html)
        this.showGrounding(data.articles)
      }).catch(() => {
        if (this.aiDraftCancelled) return
        this.showAiDraftError("Couldn't draft a reply. Try again.")
      }).finally(() => {
        this.aiDraftInFlight = false
        this.setDrafting(false)
      })
  }

  writeAiDraft(html) {
    this.editorTarget.focus()
    document.execCommand("selectAll")
    document.execCommand("insertHTML", false, this.narrowedHtml(html))
    this.syncBody()
  }

  setDrafting(busy) {
    if (this.hasAiDraftButtonTarget) {
      this.aiDraftButtonTarget.disabled = busy
      this.aiDraftButtonTarget.setAttribute("aria-busy", busy ? "true" : "false")
    }
    if (this.hasStatusTarget) this.statusTarget.textContent = busy ? "Drafting…" : ""
  }

  showAiDraftError(message) {
    if (!this.hasAiDraftErrorTarget) return

    this.aiDraftErrorTarget.textContent = message
    this.aiDraftErrorTarget.hidden = false
  }

  clearAiDraftError() {
    if (!this.hasAiDraftErrorTarget) return

    this.aiDraftErrorTarget.textContent = ""
    this.aiDraftErrorTarget.hidden = true
  }

  // ---- grounding (FRO-60) ----

  // What the prompt carried, straight from the draft response — never a later re-query, so the
  // list cannot describe articles the model was not shown. An empty set is said out loud.
  showGrounding(articles) {
    if (!this.hasGroundingTarget) return

    this.groundingArticles = Array.isArray(articles) ? articles : []
    this.groundingListTarget.replaceChildren()
    this.groundingArticles.forEach((article) => {
      this.groundingListTarget.appendChild(this.buildGroundingItem(article))
    })
    this.renderGroundingLabel()
    this.groundingTarget.hidden = false
  }

  clearGrounding() {
    if (!this.hasGroundingTarget) return

    this.groundingArticles = []
    this.groundingListTarget.replaceChildren()
    this.groundingTarget.hidden = true
  }

  renderGroundingLabel() {
    const count = this.groundingListTarget.children.length
    this.groundingTarget.classList.toggle("is-empty", count === 0)
    if (this.groundingArticles.length === 0) {
      this.groundingLabelTarget.textContent = "No knowledge articles matched — drafted from the thread alone."
    } else if (count === 0) {
      this.groundingLabelTarget.textContent = "All knowledge articles removed from this draft's list."
    } else {
      this.groundingLabelTarget.textContent = `Grounded on ${count} knowledge ${count === 1 ? "article" : "articles"}`
    }
  }

  // textContent and href only: titles and previews are admin-written text, and the URLs are
  // paths the server built. Nothing here is parsed as markup.
  buildGroundingItem(article) {
    const item = this.groundingItemTarget.content.firstElementChild.cloneNode(true)
    item.dataset.articleId = String(article.id)

    const title = item.querySelector("[data-title]")
    title.textContent = article.title
    title.href = article.url

    item.querySelector("[data-preview]").textContent = article.preview || ""
    item.querySelector("[data-resolution]").hidden = !article.from_resolution

    const source = item.querySelector("[data-source]")
    if (article.resolution_thread_url) {
      source.href = article.resolution_thread_url
      source.hidden = false
    }

    item.querySelector("[data-insert]").setAttribute("aria-label", `Insert ${article.title}`)
    item.querySelector("[data-remove]").setAttribute("aria-label", `Remove ${article.title} from this list`)
    return item
  }

  groundingArticleFor(element) {
    const item = element.closest("[data-article-id]")
    if (!item) return null

    return (this.groundingArticles || []).find((article) => String(article.id) === item.dataset.articleId)
  }

  // The picker's own insertion path, so a cite inserted from here is the same markup, the same
  // sanitizer pass and the same undo step as one picked from Insert from KB.
  insertGroundingArticle(event) {
    const article = this.groundingArticleFor(event.currentTarget)
    if (!article?.body_html) return

    if (this.savedRange) this.insertKnowledgeArticle(article)
    else this.insertKnowledgeArticleOnceCaretIsPlaced(article)
  }

  // Local to this composer: the draft is not regenerated and nothing is written back.
  removeGroundingArticle(event) {
    const item = event.currentTarget.closest("[data-article-id]")
    if (!item) return

    item.remove()
    this.renderGroundingLabel()
    this.editorTarget.focus()
  }

  toggleReplyTemplates() {
    if (this.templatesOpen) this.closeReplyTemplates()
    else this.openReplyTemplates()
  }

  get templatesOpen() {
    return this.hasTemplatesPanelTarget && !this.templatesPanelTarget.hidden
  }

  // Focus moves into the panel on open, the way it moves into the emoji palette and the URL field:
  // it is what makes the arrows, Enter and Escape work without a second Tab. The caret it leaves
  // behind is held in savedRange until a template is picked.
  //
  // The panel is shown before the fetch resolves rather than after, so pressing the button does
  // something immediately; the options land in it a moment later, and focus follows them then.
  openReplyTemplates() {
    if (!this.hasTemplatesPanelTarget) return

    // The button lists everything, so any query in flight is abandoned here rather than left to
    // re-filter this panel when its fetch lands a moment from now.
    this.templatesQuery = null
    this.closeKnowledge()
    // Captured here, from the live selection, for the reason openLink captures it here: on a
    // keyboard activation no mousedown fired, so this is the last moment the real range is readable.
    this.saveSelection()
    this.showTemplatesPanel()

    this.ensureReplyTemplates().then(() => {
      if (!this.templatesOpen) return

      this.renderReplyTemplateOptions(this.replyTemplates || [])
      this.focusReplyTemplate(0)
    })
  }

  // Shared by both ways in, because the button reports on the panel whichever one opened it — an
  // aria-expanded that only tracked presses would say "collapsed" over an open list.
  showTemplatesPanel() {
    this.templatesPanelTarget.hidden = false
    this.templatesButtonTarget.setAttribute("aria-expanded", "true")
    // Read off the element rather than restated, because the list's id is prefixed per surface.
    this.templatesButtonTarget.setAttribute("aria-controls", this.templatesListTarget.id)
  }

  // Forgotten before the guard, not after it: a query is set the moment it is typed and the panel
  // only opens once its fetch resolves, so a close in that gap has a query to clear and no panel.
  closeReplyTemplates() {
    this.templatesQuery = null
    if (!this.templatesOpen) return

    // Focus sits inside the panel whenever it was opened from the button rather than dismissed from
    // outside, and hiding the element that holds focus drops it on the body — which reads as the
    // whole composer collapsing. The editor is where the writing was going to continue anyway.
    const returnFocus = this.templatesPanelTarget.contains(document.activeElement)
    this.templatesPanelTarget.hidden = true
    this.templatesButtonTarget.setAttribute("aria-expanded", "false")
    this.editorTarget.setAttribute("aria-expanded", "false")
    this.editorTarget.removeAttribute("aria-activedescendant")
    if (returnFocus) this.editorTarget.focus()
  }

  // Once per composer, as the mentionable users are, so the typed trigger a later phase adds costs
  // no requests. A thread's resolved values cannot change under an open composer without the page
  // changing too, and a send replaces this element outright.
  ensureReplyTemplates() {
    if (this.replyTemplates) return Promise.resolve()
    if (!this.replyTemplatesUrlValue) {
      this.replyTemplates = []
      return Promise.resolve()
    }

    this.replyTemplatesFetch = this.replyTemplatesFetch || fetch(this.replyTemplatesUrlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    }).then((response) => {
      if (!response.ok) throw new Error("reply templates failed")
      return response.json()
    }).then((data) => {
      this.replyTemplates = data.templates || []
    }).catch(() => {
      this.replyTemplates = []
    })

    return this.replyTemplatesFetch
  }

  // ---- the typed trigger ----

  // A `/` and the shortcut, without the agent's hands leaving the keyboard — the motion the @
  // typeahead already has for naming a teammate. Driven off syncBody rather than off a keydown, so
  // the query is read after the character has landed in the editor rather than before it.
  refreshReplyTemplates() {
    if (!this.hasTemplatesPanelTarget || !this.isReplyMode) {
      this.closeTypedReplyTemplates()
      return
    }

    const query = this.replyTemplateQueryFromCaret()
    if (!query) {
      this.closeTypedReplyTemplates()
      return
    }

    this.templatesQuery = query
    this.ensureReplyTemplates().then(() => {
      // The agent kept typing while the first fetch was in flight, and a render for a query they
      // have already moved off would list the wrong templates. The identity check the mention
      // typeahead makes, for the same reason.
      if (this.templatesQuery !== query) return

      this.openTypedReplyTemplates()
    })
  }

  // Only the panel the typing opened. One opened from the toolbar button answers to that button and
  // lists every template, so a caret that has wandered off a query the button never had must leave it
  // alone — otherwise moving the caret would close a panel the agent had just asked for.
  closeTypedReplyTemplates() {
    if (!this.templatesQuery) return

    this.closeReplyTemplates()
  }

  // A `/` after whitespace or at the start of a text node, which is the guard mentionQueryFromCaret
  // applies to an `@` — and reusing its shape is what keeps `and/or`, `http://` and `12/03` out:
  // none of the three has whitespace in front of its slash. The query is what follows, up to the next
  // space or slash.
  //
  // The false positive it does leave is a space then a slash in ordinary prose, which opens the panel
  // and closes again as soon as what follows matches nothing — exactly what a stray `@` already does
  // in a comment.
  //
  // Containment is checked here and not in the mention half, because this runs from syncBody on every
  // surface the composer drives: the caret may be in another composer's editor entirely, and a query
  // read out of one would insert into the other.
  replyTemplateQueryFromCaret() {
    const selection = window.getSelection()
    if (!selection.rangeCount) return null

    const range = selection.getRangeAt(0)
    if (!range.collapsed) return null
    if (range.startContainer.nodeType !== Node.TEXT_NODE) return null
    if (!this.editorTarget.contains(range.startContainer)) return null

    const text = range.startContainer.textContent.slice(0, range.startOffset)
    const match = text.match(/(^|[\s\u00a0])\/([^\s/]*)$/)
    if (!match) return null

    const query = match[2]
    const start = range.startOffset - query.length - 1
    return { query, node: range.startContainer, start, end: range.startOffset }
  }

  // Closed rather than held open over a "nothing matches" line, which is the one place this departs
  // from the mention list: an `@` in prose is nearly always a name or an address, while a slash is
  // ordinary punctuation, so a panel that sat open through "and / or" would be stealing height from
  // the thread to say nothing. The next keystroke that does match opens it again.
  //
  // Focus stays in the editor, unlike the button's panel: the agent is mid-sentence, and taking the
  // caret out of the box they are typing in is the opposite of what the trigger is for. The active
  // option is announced through aria-activedescendant instead — the arrangement the mention
  // typeahead already uses, and the reason the option ids are namespaced by their list.
  openTypedReplyTemplates() {
    const matches = this.filteredReplyTemplates(this.templatesQuery.query)
    if (matches.length === 0) {
      this.closeReplyTemplates()
      return
    }

    this.showTemplatesPanel()
    this.renderReplyTemplateOptions(matches)
    this.editorTarget.setAttribute("aria-expanded", "true")
    this.editorTarget.setAttribute("aria-controls", this.templatesListTarget.id)
    this.editorTarget.setAttribute("aria-autocomplete", "list")
    this.highlightReplyTemplate(0)
  }

  // The shortcut and the name both, and anywhere in either — the same rule the mention list applies
  // to a teammate's name and address, so there is one answer to "what does a query match" in this
  // file rather than two.
  filteredReplyTemplates(query) {
    const templates = this.replyTemplates || []
    const needle = query.toLowerCase()
    if (needle === "") return templates

    return templates.filter((template) => {
      return (template.shortcut || "").includes(needle) ||
             template.name.toLowerCase().includes(needle)
    })
  }

  // Answered only for the typed panel. The button's takes focus into itself, so its keys arrive at
  // templatesKeydown on the option holding focus and never reach the editor at all.
  handleReplyTemplateKey(event) {
    if (!this.templatesQuery) return false
    if (!this.templatesOpen) return false

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveReplyTemplateHighlight(1)
      return true
    }
    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveReplyTemplateHighlight(-1)
      return true
    }
    if (event.key === "Enter" && !event.metaKey && !event.ctrlKey) {
      event.preventDefault()
      this.insertHighlightedReplyTemplate()
      return true
    }
    // The typed text is left exactly where it is: the agent dismissed a suggestion, not the sentence
    // they were writing. Answered here so it never reaches collapseTallOnEscape.
    if (event.key === "Escape") {
      event.preventDefault()
      this.closeReplyTemplates()
      return true
    }

    return false
  }

  moveReplyTemplateHighlight(delta) {
    const options = this.replyTemplateOptions
    if (options.length === 0) return

    const current = options.findIndex((option) => option.getAttribute("aria-selected") === "true")
    this.highlightReplyTemplate((current + delta + options.length) % options.length)
  }

  insertHighlightedReplyTemplate() {
    const option = this.templatesListTarget.querySelector("[aria-selected='true']")
    if (!option) return
    // A template this thread cannot fill is refused here as it is on a click. The agent is not left
    // guessing: aria-disabled and the sentence saying why are both on the option being announced.
    if (option.getAttribute("aria-disabled") === "true") return

    const template = this.replyTemplateFor(option)
    if (!template?.body_html) return

    this.replaceTypedQueryWith(template)
  }

  // The typed `/query` is put under the selection so the insertion replaces it. execCommand deletes
  // what is selected as part of the same operation, which is what leaves no stray slash behind and
  // what keeps ⌘Z taking the query and the template back out together rather than one at a time.
  replaceTypedQueryWith(template) {
    const { node, start, end } = this.templatesQuery
    const range = document.createRange()
    range.setStart(node, start)
    range.setEnd(node, end)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)

    // Before the write rather than after it, so nothing re-reads a query whose text is about to stop
    // existing. Focus is in the editor, not in the panel, so closing does not disturb the selection
    // just set.
    this.closeReplyTemplates()
    this.writeReplyTemplate(template)
  }

  // Given the templates to draw rather than deciding them, because the two ways into this panel list
  // different sets — everything, or what the query matched — and one method branching on which caller
  // it has would be the shape the composer keeps refusing elsewhere.
  renderReplyTemplateOptions(templates) {
    this.templatesListTarget.replaceChildren()
    // Only ever reached empty from the button's panel: a typed query with nothing behind it closes
    // rather than drawing an empty state, so there is no filtered emptiness for this line to describe.
    this.templatesEmptyTarget.hidden = templates.length > 0

    templates.forEach((template) => {
      this.templatesListTarget.appendChild(this.buildReplyTemplateOption(template))
    })
  }

  // A template this thread cannot fill is listed and refused rather than hidden: an agent whose
  // macro has silently vanished has no way to find out why, and the reason — "This thread has no
  // ticket id yet." — is the whole fix. aria-disabled rather than the disabled attribute, because a
  // disabled control cannot be focused, and an explanation nobody can reach is not an explanation.
  buildReplyTemplateOption(template) {
    const option = this.templateOptionTarget.content.firstElementChild.cloneNode(true)
    // Namespaced by the list, for the reason the mention options are: two composing surfaces can be
    // on the page, and one id in two places leaves one editor pointing into the other one's list.
    option.id = `${this.templatesListTarget.id}-option-${template.id}`
    option.dataset.templateId = String(template.id)
    option.querySelector("[data-name]").textContent = template.name
    option.querySelector("[data-preview]").textContent = template.preview

    if (template.shortcut) {
      const shortcut = option.querySelector("[data-shortcut]")
      shortcut.textContent = `/${template.shortcut}`
      shortcut.hidden = false
    }

    // Only on a template the project shares, so the label says "these are the team's words" rather
    // than restating the audience of every row in the list.
    if (template.shared_on) {
      const audience = option.querySelector("[data-audience]")
      audience.textContent = template.shared_on
      audience.hidden = false
    }

    // A macro says what it will do before it is picked, not after it has been sent. Only a template
    // that acts carries the line; on every other one it would be an empty row of chrome.
    if (template.action_summary) {
      const actions = option.querySelector("[data-actions]")
      actions.textContent = template.action_summary
      actions.hidden = false
    }

    if (template.body_html) return option

    option.setAttribute("aria-disabled", "true")
    option.classList.add("is-unresolvable")
    const reason = option.querySelector("[data-reason]")
    reason.textContent = template.reason
    reason.hidden = false

    return option
  }

  pickReplyTemplate(event) {
    const option = event.currentTarget
    if (option.getAttribute("aria-disabled") === "true") return

    const template = this.replyTemplateFor(option)
    if (!template?.body_html) return

    // A click on an option the typing put there replaces that typing, exactly as Enter would: the
    // agent got here by typing `/ref`, and leaving it behind would be a stray query in their reply.
    if (this.templatesQuery) {
      this.replaceTypedQueryWith(template)
      return
    }

    // Inserted before the panel is hidden, so the editor has taken focus back by the time the
    // element holding it disappears.
    if (this.savedRange) this.insertReplyTemplate(template)
    else this.insertReplyTemplateOnceCaretIsPlaced(template)
    this.closeReplyTemplates()
  }

  replyTemplateFor(option) {
    return (this.replyTemplates || [])
      .find((candidate) => String(candidate.id) === option.dataset.templateId)
  }

  insertReplyTemplate(template) {
    this.restoreSelection()
    this.writeReplyTemplate(template)
  }

  writeReplyTemplate(template) {
    // Through the paste allowlist first, because a template insertion IS a paste mechanically: this
    // is markup the editor did not produce, and "from our own server" includes a row another user
    // wrote. The endpoint sanitizes too; neither boundary trusts the other ran.
    //
    // insertHTML rather than range.insertNode, for the reason the emoji insertion gives for
    // insertText: an insertion the browser performed is one its undo stack knows about, so ⌘Z takes
    // the whole template back out the way it takes back a typed word.
    document.execCommand("insertHTML", false, this.narrowedHtml(template.body_html))
    this.armMacro(template)
    this.syncBody()
  }

  // ---- macros ----

  // A template that also acts on the thread rides along with the send as an id, and says so above
  // Send until it goes out.
  //
  // Only a template that ACTS touches any of this: inserting an ordinary one after a macro adds
  // words without withdrawing the filing the agent already asked for, so it leaves the armed macro
  // alone. Picking a second macro replaces the first — one reply performs one macro's actions,
  // because two would be two answers to where the thread should end up.
  armMacro(template) {
    if (!this.hasTemplateFieldTarget) return
    if (!template.action_summary) return

    this.templateFieldTarget.value = String(template.id)
    this.macroSummaryTarget.textContent = template.action_summary
    this.macroNoticeTarget.hidden = false
  }

  // The escape hatch: keep the words, drop what they were going to do. The editor takes focus back
  // because the button the agent just pressed is the thing being removed, and focus left on a
  // detached control lands on the document body.
  disarmMacro() {
    this.clearMacro()
    this.editorTarget.focus()
  }

  clearMacro() {
    if (!this.hasTemplateFieldTarget) return

    this.templateFieldTarget.value = ""
    this.macroSummaryTarget.textContent = ""
    this.macroNoticeTarget.hidden = true
  }

  // The paste sanitizer's verdict, back as a string, because execCommand takes markup rather than
  // nodes. Serialized through a detached element so nothing is parsed twice.
  narrowedHtml(html) {
    const holder = document.createElement("div")
    holder.appendChild(this.sanitizedFragment(html))

    return holder.innerHTML
  }

  // There is no caret to insert at when the agent reached the toolbar without ever entering the
  // editor — by Tab, since the toolbar is collapsed until something in the composer holds focus.
  // Focusing it is what places one, and in reply mode it is also what clones the sign-off and parks
  // the caret in the blank line left for the reply, a turn later. Waiting for that turn is what lands
  // the template between the greeting and "Best regards" rather than in front of both. The shape
  // insertEmojiOnceCaretIsPlaced uses, and for the same reason.
  //
  // Writing straight to the live caret rather than through restoreSelection, which is the one place
  // this departs from the emoji path: selectionchange is delivered asynchronously, so the range
  // recorded while this was focusing is a turn stale — it still describes the empty editor, which is
  // now the position in FRONT of the greeting. The caret the browser is holding by the time this runs
  // is the parked one, and it is the only one that is right.
  insertReplyTemplateOnceCaretIsPlaced(template) {
    this.focusEditorEnd()
    setTimeout(() => this.writeReplyTemplate(template), 0)
  }

  templatesKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.closeReplyTemplates()
      return
    }

    const index = this.nextReplyTemplateIndex(event)
    if (index === null) return

    event.preventDefault()
    this.focusReplyTemplate(index)
  }

  // Null for a key the list has no answer for, so the caller leaves it to the browser. Wrapped
  // rather than clamped, as the emoji palette is: a press that does nothing reads as a dead key.
  nextReplyTemplateIndex(event) {
    const options = this.replyTemplateOptions
    if (options.length === 0) return null
    if (event.key === "Home") return 0
    if (event.key === "End") return options.length - 1

    const steps = { ArrowDown: 1, ArrowUp: -1 }
    if (!(event.key in steps)) return null

    const current = options.indexOf(event.currentTarget)
    return (current + steps[event.key] + options.length) % options.length
  }

  // Which option is active, without taking focus — the typed trigger leaves the caret in the editor,
  // so there is nothing to move focus to and nothing to hand it back to afterwards.
  highlightReplyTemplate(index) {
    const options = this.replyTemplateOptions
    const target = options[index]
    if (!target) return null

    options.forEach((option) => {
      option.setAttribute("tabindex", "-1")
      option.removeAttribute("aria-selected")
    })
    target.setAttribute("aria-selected", "true")
    target.scrollIntoView({ block: "nearest" })
    this.syncTemplatesActivedescendant()

    return target
  }

  // One tab stop for the whole list, moved to whichever option is current — the same arrangement the
  // emoji palette uses, and for the same reason: a stop per template would put a growing number of
  // them between the toolbar and Send.
  focusReplyTemplate(index) {
    const target = this.highlightReplyTemplate(index)
    if (!target) return

    target.setAttribute("tabindex", "0")
    target.focus()
  }

  // Only while the caret is still in the editor, which is only ever the typed panel: the button's
  // panel puts focus on the option itself, and an activedescendant named by an element that does not
  // have focus points at nothing.
  syncTemplatesActivedescendant() {
    const active = this.templatesQuery &&
                   this.templatesListTarget.querySelector("[aria-selected='true']")
    if (active) this.editorTarget.setAttribute("aria-activedescendant", active.id)
    else this.editorTarget.removeAttribute("aria-activedescendant")
  }

  get replyTemplateOptions() {
    if (!this.hasTemplatesListTarget) return []

    return Array.from(this.templatesListTarget.querySelectorAll(".composer__template-option"))
  }

  // ---- knowledge articles ----

  // A published fact from this project's knowledge base, dropped into the reply at the
  // caret. The bodies arrive from the server already converted to the reply allowlist,
  // so nothing here knows how a textarea paragraph became a <p> — the same division
  // the template picker keeps, where the browser is handed finished markup.
  //
  // Reply mode only, and expressed as the existing isReplyMode getter rather than as
  // an entry in COMPOSER_MODES. Comment-mode insert is a later feature.

  toggleKnowledge() {
    if (this.knowledgeOpen) this.closeKnowledge()
    else this.openKnowledge()
  }

  get knowledgeOpen() {
    return this.hasKnowledgePanelTarget && !this.knowledgePanelTarget.hidden
  }

  // Focus moves into the search field on open, so typing narrows the list without a
  // second click. The caret it leaves behind is held in savedRange until an article
  // is picked. The panel is shown before the fetch resolves, the way the template
  // picker is: pressing the button does something immediately.
  openKnowledge() {
    if (!this.hasKnowledgePanelTarget) return

    this.closeReplyTemplates()
    this.closeEmoji()
    this.saveSelection()
    this.showKnowledgePanel()

    this.ensureKnowledgeArticles().then(() => {
      if (!this.knowledgeOpen) return

      this.knowledgeSearchTarget.value = ""
      this.renderKnowledgeOptions(this.knowledgeArticles || [])
      this.focusKnowledgeSearch()
    })
  }

  showKnowledgePanel() {
    this.knowledgePanelTarget.hidden = false
    this.knowledgeButtonTarget.setAttribute("aria-expanded", "true")
    this.knowledgeButtonTarget.setAttribute("aria-controls", this.knowledgeListTarget.id)
  }

  closeKnowledge() {
    if (!this.knowledgeOpen) return

    const returnFocus = this.knowledgePanelTarget.contains(document.activeElement)
    this.knowledgePanelTarget.hidden = true
    this.knowledgeButtonTarget.setAttribute("aria-expanded", "false")
    this.editorTarget.setAttribute("aria-expanded", "false")
    this.editorTarget.removeAttribute("aria-activedescendant")
    if (returnFocus) this.editorTarget.focus()
  }

  // Escape from the editor while the panel is open — the agent clicked back into
  // the box without picking. Arrows and Enter stay with the search field; they
  // must not steal keystrokes from a sentence being written.
  handleKnowledgeKey(event) {
    if (!this.knowledgeOpen) return false
    if (event.key !== "Escape") return false

    event.preventDefault()
    this.closeKnowledge()
    return true
  }

  ensureKnowledgeArticles() {
    if (this.knowledgeArticles) return Promise.resolve()
    if (!this.knowledgeArticlesUrlValue) {
      this.knowledgeArticles = []
      return Promise.resolve()
    }

    this.knowledgeArticlesFetch = this.knowledgeArticlesFetch || fetch(this.knowledgeArticlesUrlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    }).then((response) => {
      if (!response.ok) throw new Error("knowledge articles failed")
      return response.json()
    }).then((data) => {
      this.knowledgeArticles = data.articles || []
    }).catch(() => {
      this.knowledgeArticles = []
    })

    return this.knowledgeArticlesFetch
  }

  filterKnowledgeArticles() {
    if (!this.knowledgeOpen) return

    this.renderKnowledgeOptions(this.filteredKnowledgeArticles(this.knowledgeSearchTarget.value))
  }

  filteredKnowledgeArticles(query) {
    const articles = this.knowledgeArticles || []
    const needle = query.toLowerCase().trim()
    if (needle === "") return articles

    return articles.filter((article) => {
      return article.title.toLowerCase().includes(needle) ||
             (article.preview || "").toLowerCase().includes(needle)
    })
  }

  renderKnowledgeOptions(articles) {
    this.knowledgeListTarget.replaceChildren()
    const nonePublished = (this.knowledgeArticles || []).length === 0

    if (this.hasKnowledgeSearchWrapTarget) {
      this.knowledgeSearchWrapTarget.hidden = nonePublished
    }

    this.knowledgeEmptyTarget.hidden = articles.length > 0
    this.knowledgeEmptyTarget.textContent = nonePublished
      ? "No published knowledge articles yet."
      : "No articles match."

    articles.forEach((article) => {
      this.knowledgeListTarget.appendChild(this.buildKnowledgeOption(article))
    })

    if (articles.length > 0) this.highlightKnowledgeArticle(0)
  }

  buildKnowledgeOption(article) {
    const option = this.knowledgeOptionTarget.content.firstElementChild.cloneNode(true)
    option.id = `${this.knowledgeListTarget.id}-option-${article.id}`
    option.dataset.articleId = String(article.id)
    option.querySelector("[data-title]").textContent = article.title
    option.querySelector("[data-preview]").textContent = article.preview
    return option
  }

  pickKnowledgeArticle(event) {
    const article = this.knowledgeArticleFor(event.currentTarget)
    if (!article?.body_html) return

    if (this.savedRange) this.insertKnowledgeArticle(article)
    else this.insertKnowledgeArticleOnceCaretIsPlaced(article)
    this.closeKnowledge()
  }

  knowledgeArticleFor(option) {
    return (this.knowledgeArticles || [])
      .find((candidate) => String(candidate.id) === option.dataset.articleId)
  }

  insertKnowledgeArticle(article) {
    this.restoreSelection()
    this.writeKnowledgeArticle(article)
  }

  writeKnowledgeArticle(article) {
    // Through the paste allowlist first, because a knowledge insertion IS a paste
    // mechanically: this is markup the editor did not produce. The endpoint
    // sanitizes too; neither boundary trusts the other ran.
    //
    // insertHTML rather than range.insertNode, for the reason the template
    // insertion gives: an insertion the browser performed is one its undo stack
    // knows about, so undo takes the article back out.
    document.execCommand("insertHTML", false, this.narrowedHtml(article.body_html))
    this.syncBody()
  }

  insertKnowledgeArticleOnceCaretIsPlaced(article) {
    this.focusEditorEnd()
    setTimeout(() => this.writeKnowledgeArticle(article), 0)
  }

  knowledgeSearchKeydown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveKnowledgeHighlight(1)
      return
    }
    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveKnowledgeHighlight(-1)
      return
    }
    if (event.key === "Enter" && !event.metaKey && !event.ctrlKey) {
      event.preventDefault()
      this.insertHighlightedKnowledgeArticle()
      return
    }
    if (event.key === "Escape") {
      event.preventDefault()
      this.closeKnowledge()
    }
  }

  knowledgeKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.closeKnowledge()
      return
    }
    if (event.key === "Enter" && !event.metaKey && !event.ctrlKey) {
      event.preventDefault()
      this.pickKnowledgeArticle(event)
      return
    }

    const index = this.nextKnowledgeIndex(event)
    if (index === null) return

    event.preventDefault()
    this.focusKnowledgeArticle(index)
  }

  nextKnowledgeIndex(event) {
    const options = this.knowledgeArticleOptions
    if (options.length === 0) return null
    if (event.key === "Home") return 0
    if (event.key === "End") return options.length - 1

    const steps = { ArrowDown: 1, ArrowUp: -1 }
    if (!(event.key in steps)) return null

    const current = options.indexOf(event.currentTarget)
    return (current + steps[event.key] + options.length) % options.length
  }

  moveKnowledgeHighlight(delta) {
    const options = this.knowledgeArticleOptions
    if (options.length === 0) return

    const current = options.findIndex((option) => option.getAttribute("aria-selected") === "true")
    this.highlightKnowledgeArticle((current + delta + options.length) % options.length)
  }

  insertHighlightedKnowledgeArticle() {
    const option = this.knowledgeListTarget.querySelector("[aria-selected='true']")
    if (!option) return

    const article = this.knowledgeArticleFor(option)
    if (!article?.body_html) return

    if (this.savedRange) this.insertKnowledgeArticle(article)
    else this.insertKnowledgeArticleOnceCaretIsPlaced(article)
    this.closeKnowledge()
  }

  highlightKnowledgeArticle(index) {
    const options = this.knowledgeArticleOptions
    const target = options[index]
    if (!target) return null

    options.forEach((option) => {
      option.setAttribute("tabindex", "-1")
      option.removeAttribute("aria-selected")
    })
    target.setAttribute("aria-selected", "true")
    target.scrollIntoView({ block: "nearest" })
    return target
  }

  focusKnowledgeArticle(index) {
    const target = this.highlightKnowledgeArticle(index)
    if (!target) return

    target.setAttribute("tabindex", "0")
    target.focus()
  }

  focusKnowledgeSearch() {
    if (!this.hasKnowledgeSearchTarget) return
    if (this.hasKnowledgeSearchWrapTarget && this.knowledgeSearchWrapTarget.hidden) return

    this.knowledgeSearchTarget.focus()
  }

  get knowledgeArticleOptions() {
    if (!this.hasKnowledgeListTarget) return []

    return Array.from(this.knowledgeListTarget.querySelectorAll(".composer__template-option"))
  }

  // ---- paste ----

  // A paste from a webpage carries that page's markup, styles and classes. Left alone it would
  // land in the editor looking right, then be stripped by the server on the way out — so the
  // agent sees one thing and the client receives another. Narrowed here to exactly what the
  // toolbar can produce, which is exactly what ReplyBodySanitizer allows.
  //
  // Chrome's paste-and-match-style (⌘⇧V) supplies no text/html at all, so the plain-text branch
  // is reached by that shortcut without needing to inspect a modifier the event does not carry.
  paste(event) {
    const clipboard = event.clipboardData
    if (!clipboard) return

    event.preventDefault()
    if (this.pastedOnlyFiles(clipboard)) {
      this.stageFiles(clipboard.files)
      return
    }

    const html = clipboard.getData("text/html")

    if (html) this.insertFragment(this.autolinkFragment(this.sanitizedFragment(html)))
    else this.pastePlainText(clipboard.getData("text/plain"))

    this.syncBody()
  }

  // A screenshot off the clipboard carries the file and nothing else, and is an attachment — the
  // same thing dropping it would be. A copy taken from a web page carries the image AND the
  // markup around it, and that is a paste of words: attaching it would swallow the sentence the
  // agent came to paste.
  pastedOnlyFiles(clipboard) {
    if (!this.acceptsFiles || clipboard.files.length === 0) return false

    return !clipboard.getData("text/html") && !clipboard.getData("text/plain")
  }

  // insertText is on the undo stack; insertFragment is not. A paste that is only words stays
  // on insertText so ⌘Z still takes it out. A paste that contains a URL has to become markup,
  // so it takes the same insertFragment path HTML paste already does.
  pastePlainText(text) {
    if (this.urlTokensIn(text).length > 0) this.insertFragment(this.plainTextFragment(text))
    else document.execCommand("insertText", false, text)
  }

  // Newlines become the same soft break a Return produces in this editor, so a multi-line
  // paste with a URL in the middle keeps its lines rather than collapsing into one.
  plainTextFragment(text) {
    const fragment = document.createDocumentFragment()
    const lines = text.split(/\r\n|\n|\r/)

    lines.forEach((line, index) => {
      if (line !== "") fragment.appendChild(document.createTextNode(line))
      if (index < lines.length - 1) fragment.appendChild(document.createElement("br"))
    })

    return this.autolinkFragment(fragment)
  }

  // After sanitizing, so an <a> the source already provided is left alone — wrapping it
  // again would nest anchors. Text that only looks like a URL (a copied address bar, a
  // line in a pasted email) is not an <a> yet, and that is the case this exists for.
  autolinkFragment(fragment) {
    const textNodes = []
    const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_TEXT)
    let node
    while ((node = walker.nextNode())) {
      if (node.parentElement?.closest("a")) continue
      if (!node.nodeValue) continue
      textNodes.push(node)
    }
    textNodes.forEach((textNode) => this.autolinkTextNode(textNode))

    return fragment
  }

  autolinkTextNode(textNode) {
    const text = textNode.nodeValue
    const tokens = this.urlTokensIn(text)
    if (tokens.length === 0) return

    const fragment = document.createDocumentFragment()
    let cursor = 0
    tokens.forEach((token) => {
      if (token.start > cursor) {
        fragment.appendChild(document.createTextNode(text.slice(cursor, token.start)))
      }
      const href = this.autolinkHref(token.visible)
      if (href) {
        const anchor = document.createElement("a")
        anchor.setAttribute("href", href)
        anchor.appendChild(document.createTextNode(token.visible))
        fragment.appendChild(anchor)
      } else {
        fragment.appendChild(document.createTextNode(token.visible))
      }
      cursor = token.end
    })
    if (cursor < text.length) fragment.appendChild(document.createTextNode(text.slice(cursor)))

    textNode.replaceWith(fragment)
  }

  urlTokensIn(text) {
    const tokens = []
    // A fresh /g regex per call: reusing one would leave lastIndex set and skip a later token.
    const pattern = new RegExp(this.constructor.PASTE_URL_PATTERN.source, "gi")
    let match
    while ((match = pattern.exec(text)) !== null) {
      const visible = this.trimUrlToken(match[0])
      if (!this.autolinkHref(visible)) continue

      tokens.push({ start: match.index, end: match.index + visible.length, visible })
    }

    return tokens
  }

  // The same gates the toolbar uses: normalizedUrl refuses an explicit scheme we do not
  // accept, and permittedHref is the sanitizer's scheme allowlist. Either saying no
  // leaves the token as text.
  autolinkHref(value) {
    if (!/^https?:\/\/[^\s/]+/i.test(value)) return null

    const normalized = this.normalizedUrl(value)
    if (normalized === null) return null

    return this.permittedHref(normalized)
  }

  // A sentence-ending mark is almost never part of the URL. Unbalanced closers are the
  // same idea for "(see https://tracker.test/1)" — the ')' belongs to the sentence.
  // Wikipedia-style paths that open a paren keep it, because the counts match.
  trimUrlToken(token) {
    let url = token
    let previous
    do {
      previous = url
      url = url.replace(/[.,;:!?]+$/u, "")
      url = this.stripUnbalancedTrailingClosers(url)
    } while (url !== previous)

    return url
  }

  stripUnbalancedTrailingClosers(url) {
    const pairs = { ")": "(", "]": "[", "}": "{" }
    let trimmed = url
    while (trimmed.length > 0) {
      const closer = trimmed[trimmed.length - 1]
      const opener = pairs[closer]
      if (!opener) break

      const openCount = trimmed.split(opener).length - 1
      const closeCount = trimmed.split(closer).length - 1
      if (closeCount <= openCount) break
      trimmed = trimmed.slice(0, -1)
    }

    return trimmed
  }

  // Parsed into an inert document rather than assembled from strings, and rebuilt node by node
  // so nothing from the source survives except the tags and the one attribute we allow.
  sanitizedFragment(html) {
    const parsed = new DOMParser().parseFromString(html, "text/html")
    const fragment = document.createDocumentFragment()

    Array.from(parsed.body.childNodes).forEach((node) => {
      const clean = this.sanitizedNode(node)
      if (clean) fragment.appendChild(clean)
    })

    return fragment
  }

  sanitizedNode(node) {
    if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.nodeValue)
    if (node.nodeType !== Node.ELEMENT_NODE) return null

    const tag = node.tagName.toLowerCase()
    // Dropped with their contents — an allowlist that keeps the text would paste the source of
    // a script in as visible gibberish.
    if (this.constructor.PASTE_DROPPED_TAGS.includes(tag)) return null

    if (tag === "span" && this.isCommentMode && this.permittedMentionChip(node)) {
      return this.mentionChipFrom(node)
    }

    const children = Array.from(node.childNodes)
                          .map((child) => this.sanitizedNode(child))
                          .filter(Boolean)

    if (!this.constructor.PASTE_ALLOWED_TAGS.includes(tag)) {
      const opensLine = this.constructor.PASTE_LINE_TAGS.includes(tag) && !!node.previousSibling
      return this.unwrapped(children, { opensLine })
    }
    if (tag === "a" && !this.permittedHref(node.getAttribute("href"))) return this.unwrapped(children)
    // Empty paragraphs paint as blank lines even with p { margin: 0 }. Mail already
    // treats a line as a break, so a pasted <p><br></p> becomes one — comment keeps
    // the paragraph so notes stay looser.
    if (tag === "p" && this.usesSoftEnter && this.emptyPasteParagraph(children)) {
      return document.createElement("br")
    }

    const element = document.createElement(tag)
    if (tag === "a") element.setAttribute("href", this.permittedHref(node.getAttribute("href")))
    children.forEach((child) => element.appendChild(child))

    return element
  }

  // The element goes, its words stay — silently eating the text of a pasted heading would lose
  // part of what the agent meant to send.
  //
  // `opensLine` keeps the break the element stood for. Only something preceding it earns one, so a
  // paste starting with a div does not start with a blank line.
  unwrapped(children, { opensLine = false } = {}) {
    const fragment = document.createDocumentFragment()
    if (opensLine) fragment.appendChild(document.createElement("br"))
    children.forEach((child) => fragment.appendChild(child))

    return fragment
  }

  emptyPasteParagraph(children) {
    if (children.length === 0) return true

    return children.every((child) => {
      if (child.nodeType === Node.TEXT_NODE) return child.nodeValue.trim() === ""

      return child.nodeName === "BR"
    })
  }

  // Mirrors ReplyBodySanitizer#permitted_href?, including the normalization: control characters
  // and backslashes are stripped before the scheme is read, because a browser strips them too.
  permittedHref(value) {
    const href = String(value || "").replace(/[\u0000-\u0020]/g, "").replace(/\\/g, "/")
    if (href === "") return null
    if (/^\/{2,}/.test(href)) return null

    const scheme = href.match(/^([a-z][a-z0-9+.-]*):/i)
    if (!scheme) return href

    return this.constructor.PASTE_ALLOWED_SCHEMES.includes(scheme[1].toLowerCase()) ? href : null
  }

  insertFragment(fragment) {
    const selection = window.getSelection()
    if (selection.rangeCount === 0) return

    const range = selection.getRangeAt(0)
    if (!this.editorTarget.contains(range.commonAncestorContainer)) return

    // Captured before insertion: appending a fragment empties it, so afterwards there is
    // nothing left to measure the caret against.
    const lastNode = fragment.lastChild
    range.deleteContents()
    range.insertNode(fragment)
    if (!lastNode) return

    const caret = document.createRange()
    caret.setStartAfter(lastNode)
    caret.collapse(true)
    selection.removeAllRanges()
    selection.addRange(caret)
  }

  // Only while the editor itself holds focus. The link row and the emoji palette both take focus
  // away from it, and a browser then reports the editor's selection as still living inside the
  // editor — collapsed. Recording that would overwrite the very range those two panels exist to
  // insert into, which is the loss this record was introduced to prevent.
  saveSelection() {
    if (!this.editorHasFocus) return

    const selection = window.getSelection()
    if (selection.rangeCount === 0) return

    const range = selection.getRangeAt(0)
    if (!this.editorTarget.contains(range.commonAncestorContainer)) return

    this.savedRange = range.cloneRange()
  }

  restoreSelection() {
    this.editorTarget.focus()
    if (!this.savedRange) return

    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(this.savedRange)
  }

  get editorHasFocus() {
    return document.activeElement === this.editorTarget ||
           this.editorTarget.contains(document.activeElement)
  }

  // ---- toolbar keyboard navigation ----

  // Each toolbar is one tab stop; the arrows move within that toolbar. Format and Insert are
  // separate groups so Tab from the editor is format → attach → insert → Send, not every glyph.
  moveFocus(event) {
    const keys = { ArrowRight: 1, ArrowLeft: -1, Home: 0, End: 0 }
    if (!(event.key in keys)) return

    const toolbar = event.currentTarget.closest("[role=toolbar]")
    if (!toolbar) return

    event.preventDefault()
    const buttons = this.toolsIn(toolbar)
    const current = buttons.indexOf(event.currentTarget)
    let next = current + keys[event.key]
    if (event.key === "Home") next = 0
    if (event.key === "End") next = buttons.length - 1
    if (next < 0) next = buttons.length - 1
    if (next >= buttons.length) next = 0

    buttons.forEach((button) => button.setAttribute("tabindex", "-1"))
    buttons[next].setAttribute("tabindex", "0")
    buttons[next].focus()
  }

  toolsIn(toolbar) {
    return Array.from(toolbar.querySelectorAll(".composer__tool"))
      .filter((button) => !button.hidden)
  }

  // ---- attachments ----

  pickFiles() {
    this.fileInputTarget.click()
  }

  filesPicked() {
    this.stageFiles(this.fileInputTarget.files)
    // Cleared so picking the same file again still fires a change event.
    this.fileInputTarget.value = ""
  }

  // A drag is the composer's only when it is carrying files. A selection dragged out of a message
  // above, a link from the sidebar — those belong to the contenteditable underneath, which takes
  // them natively and reaches syncBody through the editor's own input handler.
  dragOverFiles(event) {
    if (!this.acceptsFiles || !this.draggingFiles(event)) return

    // A dragover left to the default means "nothing here takes this", and the browser then walks
    // away from the app to open the file. Refusing the default is what arms the drop.
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
    this.dropzoneTarget.classList.add("is-drop-target")
  }

  // dragleave bubbles up from every child the pointer crosses on its way across the box, so the
  // affordance may only lift once the pointer has left the box itself. A null relatedTarget is
  // the pointer leaving the window, which is a leave.
  dragLeaveFiles(event) {
    if (!this.acceptsFiles) return
    if (this.dropzoneTarget.contains(event.relatedTarget)) return

    this.dropzoneTarget.classList.remove("is-drop-target")
  }

  dropFiles(event) {
    if (!this.acceptsFiles || !this.draggingFiles(event)) return

    event.preventDefault()
    this.dropzoneTarget.classList.remove("is-drop-target")
    this.stageFiles(event.dataTransfer.files)
    // The chips land in a box whose footer may still be collapsed — a resting comment bar is one
    // line — and Send lives in that footer. Nothing else here opens it: the pick path is reached
    // through a button that is already in the open footer.
    this.expand()
  }

  draggingFiles(event) {
    return Array.from(event.dataTransfer?.types || []).includes("Files")
  }

  // The note's edit form runs this same controller on a box with no file strip at all, and the
  // three composers that do attach are near-identical markup — so this asks the DOM rather than
  // trusting that only the right boxes wired the handlers up.
  get acceptsFiles() {
    return this.hasDropzoneTarget && this.hasFilesListTarget
  }

  // Each batch starts a fresh verdict: a rejection from the previous one has been read and
  // acted on, and leaving it up would accuse the agent of a file they already dealt with.
  stageFiles(files) {
    this.rejectedNames = []
    Array.from(files).forEach((file) => this.addFile(file))
    this.renderRejection()
  }

  addFile(file) {
    if (this.totalBytes + file.size > this.maxBytesValue) {
      this.rejectedNames.push(file.name)
      return
    }

    const row = this.buildRow(file)
    this.filesListTarget.appendChild(row)
    this.refreshTotal()
    // Not optional: canSend() refuses while an upload is in flight, but that verdict only
    // reaches the button through update(). Without this the agent can send a reply whose
    // attachment has no signed id yet, and the file is silently left behind.
    this.update()
    this.upload(file, row)
  }

  // Re-rendered on a mode switch as well as on a pick, so a notice left over from one audience does
  // not go on naming the other one's noun.
  renderRejection() {
    if (!this.hasFilesRejectedTarget) return

    const names = this.rejectedNames
    const limit = this.humanSize(this.maxBytesValue)
    const noun = this.filesNoun
    this.filesRejectedTarget.hidden = names.length === 0

    if (names.length === 1) {
      this.filesRejectedTarget.textContent = `${names[0]} is too large — ${limit} is the limit for one ${noun}.`
    } else if (names.length > 1) {
      this.filesRejectedTarget.textContent =
        `${names.length} files are too large — ${limit} is the limit for one ${noun}.`
    }

    this.syncFilesVisibility()
  }

  // Values only — the row's structure comes from the server-rendered template, so the chips a
  // failed send restores and the ones built here cannot drift apart.
  buildRow(file) {
    const row = this.fileTemplateTarget.content.firstElementChild.cloneNode(true)
    row.querySelector(".composer__file-name").textContent = file.name
    row.querySelector(".composer__file-size").textContent = this.humanSize(file.size)
    row.querySelector(".composer__file-remove").setAttribute("aria-label", `Remove ${file.name}`)
    row.dataset.byteSize = file.size
    row.classList.toggle("composer__file--image", file.type.startsWith("image/"))
    row.classList.add("is-uploading")
    row.querySelector(".composer__file-status").textContent = "Uploading…"

    return row
  }

  // The delegate is built PER FILE, not shared: several uploads run at once and each has to write
  // into its own row. DirectUpload calls directUploadWillStoreFileWithXHR with the request it is
  // about to send, which is the only hook that sees transfer progress.
  //
  // Worth having now that a Slack reply may carry up to 100MB: at 10MB "Uploading…" was over before
  // anyone read it, and at 100MB on a slow uplink a static word is indistinguishable from a stall.
  // A browser that cannot report progress leaves the row alone rather than showing 0%.
  //
  // Written to .composer__file-progress, NOT to the .composer__file-status beside it: that node is
  // role="status" (aria-live="polite"), and a number changing a hundred times would be announced a
  // hundred times. The status node keeps the transitions worth hearing; this one is for the eye.
  uploadProgress(row) {
    return {
      directUploadWillStoreFileWithXHR(request) {
        request.upload.addEventListener("progress", (event) => {
          if (!event.lengthComputable) return

          const percent = Math.round((event.loaded / event.total) * 100)
          row.querySelector(".composer__file-progress").textContent = `${percent}%`
        })
      }
    }
  }

  upload(file, row) {
    new DirectUpload(file, this.directUploadUrlValue, this.uploadProgress(row)).create((error, blob) => {
      row.querySelector(".composer__file-progress").textContent = ""
      if (error) {
        row.classList.remove("is-uploading")
        row.classList.add("is-failed")
        row.querySelector(".composer__file-status").textContent = "Couldn't upload — remove and try again."
      } else {
        row.classList.remove("is-uploading")
        row.querySelector(".composer__file-status").textContent = ""
        row.querySelector("input[type=hidden]").value = blob.signed_id
        row.dataset.signedId = blob.signed_id
      }
      this.refreshTotal()
      this.update()
    })
  }

  removeFile(event) {
    event.currentTarget.closest(".composer__file").remove()
    this.refreshTotal()
    this.update()
  }

  refreshTotal() {
    if (!this.hasFilesTotalTarget) return

    const rows = this.fileRows
    if (rows.length === 0) {
      this.filesTotalTarget.textContent = ""
      this.filesTotalTarget.classList.remove("composer__files-total--over")
    } else {
      const total = this.totalBytes
      const over = total > this.maxBytesValue
      const noun = rows.length === 1 ? "file" : "files"
      const cap = this.humanSize(this.maxBytesValue)
      this.filesTotalTarget.classList.toggle("composer__files-total--over", over)
      this.filesTotalTarget.textContent = over
        ? `${rows.length} ${noun} · ${this.humanSize(total)} of ${cap} — remove a file to send.`
        : `${rows.length} ${noun} · ${this.humanSize(total)} of ${cap}`
    }

    this.syncFilesVisibility()
  }

  // The strip carries both the list and the rejection notice, so it stays open while either has
  // something to say — a batch where every file was refused has no rows but very much has news.
  syncFilesVisibility() {
    this.filesTarget.hidden = this.fileRows.length === 0 && this.filesRejectedTarget.hidden
  }

  // Empty rather than absent when there is no file strip at all, so the three verdicts derived from
  // it — what is uploading, what is ready, how many bytes — answer for a form that cannot attach
  // anything without each of them asking again.
  get fileRows() {
    if (!this.hasFilesListTarget) return []

    return Array.from(this.filesListTarget.querySelectorAll(".composer__file"))
  }

  get uploadingRows() {
    return this.fileRows.filter((row) => row.classList.contains("is-uploading"))
  }

  get readyRows() {
    return this.fileRows.filter((row) => !row.classList.contains("is-uploading") &&
                                         !row.classList.contains("is-failed"))
  }

  get totalBytes() {
    return this.fileRows.reduce((sum, row) => sum + Number(row.dataset.byteSize || 0), 0)
  }

  // Matches the server's number_to_human_size closely enough that the running total and the
  // failure message an over-cap send returns do not read as two different numbers.
  humanSize(bytes) {
    if (bytes < 1024) return `${bytes} Bytes`
    const units = ["KB", "MB", "GB"]
    let size = bytes / 1024
    let unit = 0
    while (size >= 1024 && unit < units.length - 1) {
      size /= 1024
      unit += 1
    }

    return `${size < 10 ? size.toFixed(1) : Math.round(size)} ${units[unit]}`
  }

  // The baseline has to mean "what was already on the thread when I started writing", and the
  // server can only stamp it when the pane renders. Reply rests collapsed for as long as the
  // agent reads, and a reply that arrives live in that window is one they have seen — warning
  // about it on send claimed a teammate had answered during a compose that had not begun
  // (FRO-94). So the clock is restarted here, at the moment Reply becomes the box being written
  // in. The server is told the same thing, since this field is what a send posts.
  //
  // Only on the collapsed → open step. Re-stamping a composer that is already holding the
  // agent's words would swallow the reply that landed mid-sentence, which is the one collision
  // this whole path exists to interrupt.
  stampSeenOutbound() {
    if (!this.hasSeenOutboundFieldTarget) return

    this.seenOutboundFieldTarget.value = this.currentHumanOutboundId()
  }

  // Zero is a baseline like any other — it says the thread carried no reply when the box
  // opened, which is the race two agents answering a fresh thread run into. Whether anything
  // newer landed after it is the server's question alone: DetectStaleReplyComposeService reads
  // this baseline off the posted form and refuses the send if a teammate's reply outran it.
  // The check deliberately does NOT also live here — it did once, and keeping two copies of it
  // identical is what kept failing (FRO-93, FRO-94).
  currentHumanOutboundId() {
    const thread = this.element.closest(".thread")
    let latest = Number(thread?.dataset.lastHumanOutboundId || 0)
    this.humanOutboundCards.forEach((card) => {
      const id = Number(card.dataset.humanOutboundId)
      if (id > latest) latest = id
    })
    return latest
  }

  // Card roots only. The attribute belongs to a message card, and reading it off anything else
  // under the thread would let unrelated markup that happens to carry it decide whether a send
  // is interrupted.
  get humanOutboundCards() {
    const thread = this.element.closest(".thread")
    return Array.from(thread?.querySelectorAll(".message-card[data-human-outbound-id]") || [])
  }

  refreshThread(event) {
    event.preventDefault()
    if (!this.hasConversationIdValue) return

    window.Turbo.visit(`/conversations/${this.conversationIdValue}`, { frame: "conversation" })
  }

  sendAnyway(event) {
    event.preventDefault()
    if (!this.hasConfirmStaleSendFieldTarget) return

    this.confirmStaleSendFieldTarget.value = "1"
    this.element.requestSubmit()
  }

  // ---- in-flight ----

  sending(event) {
    this.syncBody()
    if (!this.commitAndValidateRecipients()) {
      event.preventDefault()
      const message = "One of the recipient addresses isn't valid."
      if (this.hasStatusTarget) this.statusTarget.textContent = message
      if (this.hasHintTarget) this.hintTarget.textContent = message
      return
    }
    // The editor is about to stop being editable, and a palette whose glyphs would silently go
    // nowhere is worse than one that is closed.
    this.closeEmoji()
    this.closeReplyTemplates()
    this.closeKnowledge()
    // Forgotten before the request returns: a successful send replaces this composer, and
    // disconnect would otherwise stash the body that just went out.
    this.draftDiscarded = true
    this.abortServerDraft()
    this.forgetDraft()
    this.stopTyping({ immediate: true })
    this.element.classList.add("is-sending")
    this.element.setAttribute("aria-busy", "true")
    this.submitTarget.disabled = true
    // There is no readonly on a contenteditable. The value is already mirrored into the hidden
    // field, so unlike the old textarea there is nothing here that not-submitting could lose.
    this.editorTarget.setAttribute("contenteditable", "false")
    this.setToolsDisabled(true)
    // Announced, not shown: the spinner that replaces the send glyph is the visual signal, and
    // it is aria-hidden, so this live region is what a screen reader actually gets.
    this.statusTarget.textContent = "Sending…"
  }

  // A successful or rejected send replaces this whole composer, so clearing the state is
  // usually moot. It is not moot when the request never lands — a dropped connection or a
  // Turbo fetch error ends the submission with nothing to swap in, and without this the
  // spinner would spin forever over an editor the agent can no longer type in.
  sent(event) {
    if (!event.detail?.success) {
      // A dropped request never replaces the composer, so the stash has to start accepting
      // writes again or navigating away would lose what is still in the box.
      this.draftDiscarded = false
      this.saveDraft()
      this.persistServerDraft({ immediate: true })
    }
    this.element.classList.remove("is-sending")
    this.element.removeAttribute("aria-busy")
    this.editorTarget.setAttribute("contenteditable", "true")
    this.setToolsDisabled(false)
    this.statusTarget.textContent = ""
    this.update()
  }

  setToolsDisabled(disabled) {
    this.element.querySelectorAll(".composer__tool").forEach((button) => {
      button.disabled = disabled
    })
  }

  // ---- mentions ----

  get isCommentMode() {
    return this.element.dataset.mode === "comment"
  }

  get mentionsOpen() {
    return this.hasMentionsTarget && !this.mentionsTarget.hidden
  }

  // Comment names teammates (and notifies them). Reply and Compose insert a mailto
  // link from the same roster To / Cc already search — address linking, not @notify.
  get canMention() {
    if (!this.hasMentionsTarget) return false
    if (this.isCommentMode) return Boolean(this.mentionableUrlValue)
    return Boolean(this.recipientSuggestionsUrlValue)
  }

  refreshMentions() {
    if (!this.canMention) {
      this.closeMentions()
      return
    }

    const query = this.mentionQueryFromCaret()
    if (!query) {
      this.closeMentions()
      return
    }

    this.activeMentionQuery = query
    if (this.isCommentMode) {
      this.ensureMentionableUsers().then(() => {
        if (this.activeMentionQuery !== query) return
        this.renderMentionOptions(this.filteredMentionableUsers(query.query))
      })
      return
    }

    this.scheduleReplyMentionSuggestions(query)
  }

  // An @ after whitespace (or at the start of a text node), not the one in an email address
  // and not one already inside a chip or a link. The query is whatever follows, up to the next space.
  mentionQueryFromCaret() {
    const selection = window.getSelection()
    if (!selection.rangeCount) return null

    const range = selection.getRangeAt(0)
    if (!range.collapsed) return null
    if (range.startContainer.nodeType !== Node.TEXT_NODE) return null
    if (range.startContainer.parentElement?.closest("a, .comment-mention")) return null

    const text = range.startContainer.textContent.slice(0, range.startOffset)
    const match = text.match(/(^|[\s\u00a0])@([^\s@]*)$/)
    if (!match) return null

    const query = match[2]
    const start = range.startOffset - query.length - 1
    return { query, node: range.startContainer, start, end: range.startOffset }
  }

  ensureMentionableUsers() {
    if (this.mentionableUsers) return Promise.resolve()
    if (!this.mentionableUrlValue) return Promise.resolve()

    this.mentionableFetch = this.mentionableFetch || fetch(this.mentionableUrlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    }).then((response) => {
      if (!response.ok) throw new Error("mentionable users failed")
      return response.json()
    }).then((data) => {
      this.mentionableUsers = data.users || []
    }).catch(() => {
      this.mentionableUsers = []
    })

    return this.mentionableFetch
  }

  scheduleReplyMentionSuggestions(query) {
    this.clearReplyMentionDebounce()
    this.replyMentionDebounce = window.setTimeout(() => {
      this.fetchReplyMentionSuggestions(query)
    }, 200)
  }

  clearReplyMentionDebounce() {
    if (this.replyMentionDebounce) {
      window.clearTimeout(this.replyMentionDebounce)
      this.replyMentionDebounce = null
    }
  }

  fetchReplyMentionSuggestions(query) {
    this.abortReplyMentionSuggestions()
    const request = new AbortController()
    this.replyMentionAbort = request

    fetch(this.replyMentionSuggestionsUrl(query.query), {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      signal: request.signal
    }).then((response) => {
      if (!response.ok) throw new Error("mention suggestions failed")
      return response.json()
    }).then((data) => {
      if (this.activeMentionQuery !== query) return
      this.renderMentionOptions(data.suggestions || [])
    }).catch((error) => {
      if (error.name === "AbortError") return
      if (this.activeMentionQuery === query) this.closeMentions()
    })
  }

  abortReplyMentionSuggestions() {
    if (!this.replyMentionAbort) return

    this.replyMentionAbort.abort()
    this.replyMentionAbort = null
  }

  replyMentionSuggestionsUrl(query) {
    const url = new URL(this.recipientSuggestionsUrlValue, window.location.origin)
    url.searchParams.set("q", query)
    url.searchParams.set("allow_blank", "1")
    if (this.isComposeMode && this.hasFromFieldTarget && this.fromFieldTarget.value) {
      url.searchParams.set("mailbox_id", this.fromFieldTarget.value)
    }
    return url.toString()
  }

  renderMentionOptions(matches) {
    this.mentionsListTarget.replaceChildren()
    this.mentionsEmptyTarget.hidden = matches.length > 0

    matches.forEach((entry, index) => {
      const option = this.mentionOptionTarget.content.firstElementChild.cloneNode(true)
      // Namespaced by the list, not by the person alone: the composer and an open edit form can both
      // be on the page, and two options with one id would leave one editor's aria-activedescendant
      // pointing into the other one's list. Reply rows are keyed by index because a contact has no
      // user id.
      const identity = entry.id == null ? index : entry.id
      option.id = `${this.mentionsListTarget.id}-option-${identity}`
      if (entry.id != null) option.dataset.userId = String(entry.id)
      option.dataset.name = entry.name || ""
      option.dataset.email = entry.email || ""
      this.stampMentionAvatar(option, entry.avatar_seed)
      option.querySelector("[data-name]").textContent = entry.name || entry.email || ""
      const email = option.querySelector("[data-email]")
      email.textContent = entry.email || ""
      email.hidden = !entry.name || !entry.email || entry.name === entry.email
      if (index === 0) option.setAttribute("aria-selected", "true")
      this.mentionsListTarget.appendChild(option)
    })

    this.mentionsTarget.hidden = false
    this.editorTarget.setAttribute("aria-expanded", "true")
    // Read off the element rather than restated, for the reason the option ids are namespaced above.
    this.editorTarget.setAttribute("aria-controls", this.mentionsListTarget.id)
    this.editorTarget.setAttribute("aria-autocomplete", "list")
    this.syncMentionActivedescendant()
  }

  stampMentionAvatar(option, seed) {
    const avatar = option.querySelector("[data-avatar-seed]")
    if (!avatar) return

    if (seed) {
      avatar.hidden = false
      avatar.dataset.userAvatarSeedValue = seed
      return
    }

    avatar.hidden = true
  }

  filteredMentionableUsers(query) {
    const users = this.mentionableUsers || []
    const needle = query.toLowerCase()
    if (needle === "") return users

    return users.filter((user) => {
      return user.name.toLowerCase().includes(needle) || user.email.toLowerCase().includes(needle)
    })
  }

  handleMentionKey(event) {
    if (!this.mentionsOpen) return false

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveMentionHighlight(1)
      return true
    }
    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveMentionHighlight(-1)
      return true
    }
    if (event.key === "Enter" && !event.metaKey && !event.ctrlKey) {
      event.preventDefault()
      this.pickHighlightedMention()
      return true
    }
    if (event.key === "Escape") {
      event.preventDefault()
      this.closeMentions()
      return true
    }

    return false
  }

  mentionOptions() {
    return Array.from(this.mentionsListTarget.querySelectorAll(".composer__mention-option"))
  }

  moveMentionHighlight(delta) {
    const options = this.mentionOptions()
    if (options.length === 0) return

    const current = options.findIndex((option) => option.getAttribute("aria-selected") === "true")
    const next = (current + delta + options.length) % options.length
    options.forEach((option, index) => {
      if (index === next) option.setAttribute("aria-selected", "true")
      else option.removeAttribute("aria-selected")
    })
    options[next].scrollIntoView({ block: "nearest" })
    this.syncMentionActivedescendant()
  }

  syncMentionActivedescendant() {
    const selected = this.mentionsListTarget.querySelector("[aria-selected='true']")
    if (selected) this.editorTarget.setAttribute("aria-activedescendant", selected.id)
    else this.editorTarget.removeAttribute("aria-activedescendant")
  }

  pickMention(event) {
    this.insertMention({
      id: event.currentTarget.dataset.userId,
      name: event.currentTarget.dataset.name,
      email: event.currentTarget.dataset.email
    })
  }

  pickHighlightedMention() {
    const selected = this.mentionsListTarget.querySelector("[aria-selected='true']")
    if (!selected) return

    this.insertMention({
      id: selected.dataset.userId,
      name: selected.dataset.name,
      email: selected.dataset.email
    })
  }

  insertMention(entry) {
    if (this.isCommentMode) {
      this.insertCommentMention(entry)
      return
    }

    this.insertMailtoMention(entry)
  }

  insertCommentMention(user) {
    if (!this.hasMentionChipTarget) return

    const chip = this.mentionChipTarget.content.firstElementChild.cloneNode(true)
    chip.dataset.userId = String(user.id)
    chip.textContent = `@${user.name}`
    this.replaceMentionQueryWith(chip)
  }

  // A real mailto anchor, not a comment chip — outbound mail must not carry a
  // data-user-id or create a CommentMention.
  insertMailtoMention(entry) {
    if (!this.hasMentionMailtoTarget) return

    const email = (entry.email || "").trim()
    if (!/^[^@\s]+@[^@\s]+$/.test(email)) return

    const link = this.mentionMailtoTarget.content.firstElementChild.cloneNode(true)
    link.setAttribute("href", `mailto:${email}`)
    link.textContent = (entry.name || "").trim() || email
    this.replaceMentionQueryWith(link)
  }

  replaceMentionQueryWith(node) {
    const query = this.activeMentionQuery
    if (!query) return

    const { node: textNode, start, end } = query
    const before = textNode.textContent.slice(0, start)
    const after = textNode.textContent.slice(end)
    const parent = textNode.parentNode
    const space = document.createTextNode(" ")
    const afterNode = document.createTextNode(after)
    textNode.textContent = before
    parent.insertBefore(node, textNode.nextSibling)
    parent.insertBefore(space, node.nextSibling)
    parent.insertBefore(afterNode, space.nextSibling)

    const caret = document.createRange()
    caret.setStart(space, 1)
    caret.collapse(true)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(caret)

    this.closeMentions()
    this.syncBody()
  }

  // Nothing to close means nothing to clear, and that guard is load-bearing: the editor's
  // aria-expanded and aria-activedescendant are shared with the template typeahead — so
  // clearing them unconditionally would blank the state of a panel this half is not driving.
  closeMentions() {
    this.activeMentionQuery = null
    this.clearReplyMentionDebounce()
    this.abortReplyMentionSuggestions()
    if (!this.mentionsOpen) return

    this.mentionsTarget.hidden = true
    this.mentionsListTarget.replaceChildren()
    this.editorTarget.setAttribute("aria-expanded", "false")
    this.editorTarget.removeAttribute("aria-activedescendant")
  }

  unwrapMentionChips() {
    this.editorTarget.querySelectorAll("span.comment-mention").forEach((chip) => {
      chip.replaceWith(document.createTextNode(chip.textContent))
    })
  }

  permittedMentionChip(node) {
    const userId = node.getAttribute("data-user-id") || ""
    return node.classList?.contains("comment-mention") && /^\d+$/.test(userId)
  }

  mentionChipFrom(node) {
    const chip = this.mentionChipTarget.content.firstElementChild.cloneNode(true)
    chip.dataset.userId = node.getAttribute("data-user-id")
    chip.textContent = node.textContent.replace(/\s+/g, " ").trim()
    return chip
  }

  // ---- drafts ----

  // Per conversation and per mode. localStorage is the fast stash for this browser;
  // the server copy is what follows the agent to another machine. On connect, an empty
  // local stash takes the server row; if both have words, the newer updated_at /
  // savedAt wins. A refused send re-renders the composer with the body already in it,
  // and that render wins over whatever we stashed — it is newer.

  // Stimulus actions pass the event as the first argument. A binding that called
  // saveDraft directly used to persist that Event as `mode`, so a To/Cc edit
  // wrote { isTrusted: false } instead of "compose".
  composerMode(mode) {
    return typeof mode === "string" && mode !== "" ? mode : this.element.dataset.mode
  }

  draftKey(mode = this.element.dataset.mode) {
    mode = this.composerMode(mode)
    if (!this.userIdValue || !mode) return null
    if (this.isComposeMode) {
      return `${this.constructor.DRAFT_PREFIX}.${this.userIdValue}.compose`
    }
    if (!this.conversationIdValue) return null

    return `${this.constructor.DRAFT_PREFIX}.${this.userIdValue}.${this.conversationIdValue}.${mode}`
  }

  saveDraft(mode = this.element.dataset.mode) {
    mode = this.composerMode(mode)
    if (!this.draftReady || this.draftDiscarded || this.suppressDraftSave) return

    const key = this.draftKey(mode)
    if (!key) return

    try {
      if (!this.draftable()) {
        localStorage.removeItem(key)
        this.persistServerDraft({ immediate: true, mode })
        return
      }

      localStorage.setItem(key, JSON.stringify({
        body: this.editorTarget.innerHTML,
        files: this.readyRows.map((row) => this.fileSnapshot(row)).filter((file) => file.signedId),
        messageId: this.anchorMessageId(),
        excerpt: this.anchorExcerpt(),
        savedAt: new Date().toISOString(),
        ...this.recipientsSnapshot()
      }))
      this.persistServerDraft({ mode })
    } catch {
      // Quota or a locked store must not take typing down with it.
    }
  }

  restoreDraft({ force = false } = {}) {
    if (!force && this.hasContent()) return false

    const draft = this.readDraft()
    if (!draft) return false

    this.applyDraft(draft)
    return true
  }

  applyDraft(draft) {
    this.editorTarget.innerHTML = draft.body || ""
    if (!this.isCommentMode) this.unwrapMentionChips()
    this.replaceFiles(draft.files || [])
    this.restoreRecipients(draft)
    this.restoreAnchor(draft)
    this.signedOff = this.hasBodyText()
    this.syncBody()
  }

  readDraft(mode = this.element.dataset.mode) {
    const key = this.draftKey(mode)
    if (!key) return null

    try {
      const raw = localStorage.getItem(key)
      if (!raw) return null

      return this.normalizeDraft(JSON.parse(raw))
    } catch {
      return null
    }
  }

  normalizeDraft(draft, { server = false } = {}) {
    if (!draft || typeof draft !== "object") return null

    const savedAt = typeof draft.savedAt === "string" ? draft.savedAt : ""
    const updatedAt = typeof draft.updatedAt === "string" ? draft.updatedAt : ""

    return {
      body: typeof draft.body === "string" ? draft.body : "",
      files: Array.isArray(draft.files) ? draft.files : [],
      messageId: typeof draft.messageId === "string" ? draft.messageId : "",
      excerpt: typeof draft.excerpt === "string" ? draft.excerpt : "",
      to: typeof draft.to === "string" ? draft.to : null,
      cc: typeof draft.cc === "string" ? draft.cc : null,
      bcc: typeof draft.bcc === "string" ? draft.bcc : null,
      fromMailboxId: typeof draft.fromMailboxId === "string" ? draft.fromMailboxId : null,
      subject: typeof draft.subject === "string" ? draft.subject : null,
      savedAt,
      updatedAt,
      server
    }
  }

  forgetDraft(mode = this.element.dataset.mode) {
    const key = this.draftKey(mode)
    if (!key) return

    try { localStorage.removeItem(key) } catch { /* same as saveDraft */ }
  }

  writeLocalDraft(mode, draft, savedAt) {
    const key = this.draftKey(mode)
    if (!key || !draft) return

    try {
      localStorage.setItem(key, JSON.stringify({
        body: draft.body || "",
        files: draft.files || [],
        messageId: draft.messageId || "",
        excerpt: draft.excerpt || "",
        to: draft.to,
        cc: draft.cc,
        bcc: draft.bcc,
        fromMailboxId: draft.fromMailboxId,
        subject: draft.subject,
        savedAt: savedAt || draft.updatedAt || draft.savedAt || new Date().toISOString()
      }))
    } catch {
      // same as saveDraft
    }
  }

  serverDraft(mode = this.element.dataset.mode) {
    if (!this.hasServerDraftsValue || !this.serverDraftsValue) return null

    return this.normalizeDraft(this.serverDraftsValue[mode], { server: true })
  }

  rememberServerDraft(mode, payload) {
    const drafts = { ...(this.hasServerDraftsValue ? this.serverDraftsValue : {}) }
    if (payload) drafts[mode] = payload
    else delete drafts[mode]
    this.serverDraftsValue = drafts
  }

  reconcileServerDraft(mode = this.element.dataset.mode) {
    const local = this.readDraft(mode)
    const server = this.serverDraft(mode)
    const winner = this.newerDraft(local, server)
    if (winner && winner.server) {
      this.applyDraft(winner)
      this.writeLocalDraft(mode, winner, winner.updatedAt)
      return
    }
    if (winner && !server) this.pendingServerPush = mode
    if (winner && server && !winner.server) this.pendingServerPush = mode
  }

  newerDraft(local, server) {
    if (!local) return server
    if (!server) return local

    return this.draftTimestamp(local) >= this.draftTimestamp(server) ? local : server
  }

  draftTimestamp(draft) {
    const raw = draft.savedAt || draft.updatedAt
    const milliseconds = Date.parse(raw)
    return Number.isFinite(milliseconds) ? milliseconds : 0
  }

  persistServerDraft({ immediate = false, keepalive = false, mode = this.element.dataset.mode } = {}) {
    mode = this.composerMode(mode)
    if (!this.hasDraftUrlValue || !this.draftUrlValue) return Promise.resolve()
    if (this.draftDiscarded) return Promise.resolve()

    window.clearTimeout(this.serverDraftTimer)
    this.serverDraftTimer = null
    if (!immediate) {
      this.serverDraftTimer = window.setTimeout(() => {
        this.flushServerDraft(mode)
      }, DRAFT_PERSIST_MS)
      return Promise.resolve()
    }

    return this.flushServerDraft(mode, { keepalive })
  }

  flushServerDraft(mode, { keepalive = false } = {}) {
    window.clearTimeout(this.serverDraftTimer)
    this.serverDraftTimer = null
    this.abortServerDraft()

    const draft = this.readDraft(mode)
    if (!draft) {
      return this.deleteServerDraft(mode, { keepalive })
    }

    return this.putServerDraft(mode, draft, { keepalive })
  }

  abortServerDraft() {
    window.clearTimeout(this.serverDraftTimer)
    this.serverDraftTimer = null
    this.serverDraftAbort?.abort()
    this.serverDraftAbort = null
  }

  putServerDraft(mode, draft, { keepalive = false } = {}) {
    const abort = keepalive ? null : new AbortController()
    if (abort) this.serverDraftAbort = abort

    return fetch(this.draftUrlValue, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken()
      },
      body: JSON.stringify({
        mode,
        body_html: draft.body || "",
        files: (draft.files || []).map((file) => ({
          signed_id: file.signedId,
          filename: file.filename,
          byte_size: file.byteSize,
          image: file.image
        })),
        to_emails: draft.to,
        cc_emails: draft.cc,
        bcc_emails: draft.bcc,
        from_mailbox_id: draft.fromMailboxId,
        subject: draft.subject,
        message_id: draft.messageId,
        excerpt: draft.excerpt,
        saved_at: draft.savedAt || new Date().toISOString()
      }),
      credentials: "same-origin",
      keepalive,
      signal: abort?.signal
    }).then((response) => {
      if (!response.ok) return null
      if (response.status === 204) return { draft: null }
      return response.json()
    }).then((data) => {
      if (!data) return
      this.rememberServerDraft(mode, data.draft)
      if (data.draft?.updatedAt) {
        const current = this.readDraft(mode)
        if (current) this.writeLocalDraft(mode, current, data.draft.updatedAt)
      }
    }).catch(() => {
      // The next keystroke retries; a dropped persist must not surface as a page error.
    })
  }

  deleteServerDraft(mode, { keepalive = false } = {}) {
    const abort = keepalive ? null : new AbortController()
    if (abort) this.serverDraftAbort = abort

    const target = `${this.draftUrlValue}?mode=${encodeURIComponent(mode || "")}`
    return fetch(target, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      credentials: "same-origin",
      keepalive,
      signal: abort?.signal
    }).then((response) => {
      if (response.ok) this.rememberServerDraft(mode, null)
    }).catch(() => {})
  }

  draftable() {
    if (this.hasBodyText()) return true
    if (this.editorTarget.querySelector("ol, ul, a, .comment-mention")) return true
    if (this.recipientsDirty()) return true
    if (this.hasSubject()) return true
    if (this.anchorMessageId()) return true

    return this.readyRows.length > 0
  }

  restoreAnchor(draft) {
    if (!this.isCommentMode) {
      this.setAnchor("", "")
      return
    }

    this.setAnchor(draft.messageId || "", draft.excerpt || "")
  }

  anchorMessageId() {
    return this.hasMessageFieldTarget ? this.messageFieldTarget.value : ""
  }

  anchorExcerpt() {
    return this.hasAnchorExcerptTarget ? this.anchorExcerptTarget.textContent : ""
  }

  recipientsSnapshot() {
    if (!this.hasToFieldTarget) return {}

    return {
      to: this.toFieldTarget.value,
      cc: this.hasCcFieldTarget ? this.ccFieldTarget.value : "",
      bcc: this.hasBccFieldTarget ? this.bccFieldTarget.value : "",
      fromMailboxId: this.hasFromFieldTarget ? this.fromFieldTarget.value : "",
      subject: this.hasSubjectFieldTarget ? this.subjectFieldTarget.value : ""
    }
  }

  restoreRecipients(draft) {
    if (!this.hasToFieldTarget) return

    if (draft.to != null) this.setRecipientField(this.toFieldTarget, draft.to)
    if (draft.cc != null && this.hasCcFieldTarget) this.setRecipientField(this.ccFieldTarget, draft.cc)
    if (draft.bcc != null && this.hasBccFieldTarget) {
      this.setRecipientField(this.bccFieldTarget, draft.bcc)
      if (draft.bcc) this.showBcc()
    }
    if (draft.fromMailboxId != null && this.hasFromFieldTarget) {
      this.fromFieldTarget.value = draft.fromMailboxId
    }
    if (draft.subject != null && this.hasSubjectFieldTarget) {
      this.subjectFieldTarget.value = draft.subject
    }
  }

  resetSubject() {
    if (this.hasSubjectFieldTarget) this.subjectFieldTarget.value = ""
  }

  resetRecipients() {
    this.recipientFields.forEach((field) => {
      if (field.dataset.default != null) this.setRecipientField(field, field.dataset.default)
    })
    if (this.hasBccRowTarget && this.hasBccFieldTarget && !this.bccFieldTarget.value) {
      this.bccRowTarget.hidden = true
      if (this.hasBccToggleTarget) {
        this.bccToggleTarget.hidden = false
        this.bccToggleTarget.setAttribute("aria-expanded", "false")
      }
    }
  }

  recipientsDirty() {
    return this.recipientFields.some((field) => field.value !== (field.dataset.default || ""))
  }

  get recipientFields() {
    const fields = []
    if (this.hasFromFieldTarget) fields.push(this.fromFieldTarget)
    if (this.hasToFieldTarget) fields.push(this.toFieldTarget)
    if (this.hasCcFieldTarget) fields.push(this.ccFieldTarget)
    if (this.hasBccFieldTarget) fields.push(this.bccFieldTarget)
    return fields
  }

  setRecipientField(field, value) {
    field.value = value ?? ""
    if (!field.matches("[data-recipient-chips-target='field']")) return

    // Setting .value does not notify the chip controller. Change is what
    // rebuilds chips; input is what drafts already listen for.
    field.dispatchEvent(new Event("change", { bubbles: true }))
  }

  focusRecipientInput(field) {
    if (!field) return

    const input = field.closest("[data-controller~='recipient-chips']")
      ?.querySelector("[data-recipient-chips-target='input']")
    ;(input || field).focus()
  }

  commitAndValidateRecipients() {
    let valid = true
    let firstInvalid = null
    this.element.querySelectorAll("[data-controller~='recipient-chips']").forEach((element) => {
      const control = this.application.getControllerForElementAndIdentifier(element, "recipient-chips")
      if (!control) return

      control.commitPending()
      if (!control.hasInvalidChips) return

      valid = false
      firstInvalid ||= control
    })
    firstInvalid?.focusInvalid()
    return valid
  }

  clearComposer() {
    this.editorTarget.innerHTML = ""
    this.replaceFiles([])
    this.rejectedNames = []
    this.signedOff = false
    this.resetRecipients()
    this.resetSubject()
    this.setAnchor("", "")
    this.syncBody()
    this.renderRejection()
  }

  // ---- message anchor ----

  // Reply / Reply all / Forward from a specific card. Recipients and the parent
  // id come from that card. Latest-message Reply / Reply all use this too;
  // Slack's Reply still calls open() and leaves both alone.
  composeFromMessage({
    to = "",
    cc = "",
    bodyHtml = "",
    parentMessageId = "",
    asNewConversation = false,
    replaceBody = false
  } = {}) {
    if (!this.isReplyMode) return

    this.element.action = asNewConversation && this.forwardsUrlValue
      ? this.forwardsUrlValue
      : (this.messagesUrlValue || this.element.action)
    if (asNewConversation) this.element.setAttribute("data-turbo-frame", "_top")
    else this.element.removeAttribute("data-turbo-frame")

    if (this.hasParentMessageFieldTarget) {
      this.parentMessageFieldTarget.value = parentMessageId || ""
    }
    if (this.hasToFieldTarget) this.setRecipientField(this.toFieldTarget, to)
    if (this.hasCcFieldTarget) this.setRecipientField(this.ccFieldTarget, cc)
    if (this.hasBccFieldTarget) this.setRecipientField(this.bccFieldTarget, "")
    if (this.hasBccRowTarget && this.hasBccToggleTarget && this.hasBccFieldTarget) {
      this.bccRowTarget.hidden = true
      this.bccToggleTarget.hidden = false
      this.bccToggleTarget.setAttribute("aria-expanded", "false")
    }
    if (replaceBody) {
      this.editorTarget.replaceChildren()
      if (bodyHtml) this.editorTarget.append(this.sanitizedFragment(bodyHtml))
      this.signedOff = this.hasBodyText()
      this.syncBody()
    }
    this.open({ keepCompose: true })
    if (!to && this.hasToFieldTarget) this.focusRecipientInput(this.toFieldTarget)
  }

  resetComposeTarget() {
    if (this.messagesUrlValue) this.element.action = this.messagesUrlValue
    this.element.removeAttribute("data-turbo-frame")
    if (this.hasParentMessageFieldTarget) this.parentMessageFieldTarget.value = ""
  }

  // Named from a message card's "Comment on this". This form is already the
  // comment bar, so we only set the chip and park the caret.
  anchorTo({ messageId, excerpt }) {
    if (!this.hasMessageFieldTarget) return

    this.setAnchor(messageId, excerpt)
    this.saveDraft()
    this.expand()
    this.focusEditorEnd()
  }

  clearAnchor() {
    this.setAnchor("", "")
    this.saveDraft()
  }

  setAnchor(messageId, excerpt) {
    if (!this.hasMessageFieldTarget) return

    this.messageFieldTarget.value = messageId || ""
    if (this.hasAnchorExcerptTarget) this.anchorExcerptTarget.textContent = excerpt || ""
    if (this.hasAnchorTarget) this.anchorTarget.hidden = !messageId
  }

  fileSnapshot(row) {
    return {
      signedId: row.dataset.signedId,
      filename: row.querySelector(".composer__file-name")?.textContent || "",
      byteSize: Number(row.dataset.byteSize || 0),
      image: row.classList.contains("composer__file--image")
    }
  }

  replaceFiles(files) {
    this.filesListTarget.replaceChildren()
    files.forEach((file) => {
      if (!file?.signedId) return
      this.filesListTarget.appendChild(this.restoredFileRow(file))
    })
    this.refreshTotal()
  }

  restoredFileRow(file) {
    const row = this.fileTemplateTarget.content.firstElementChild.cloneNode(true)
    const filename = file.filename || "file"
    row.querySelector(".composer__file-name").textContent = filename
    row.querySelector(".composer__file-size").textContent = this.humanSize(Number(file.byteSize || 0))
    row.querySelector(".composer__file-remove").setAttribute("aria-label", `Remove ${filename}`)
    row.dataset.byteSize = Number(file.byteSize || 0)
    row.dataset.signedId = file.signedId
    row.querySelector("input[type=hidden]").value = file.signedId
    row.classList.toggle("composer__file--image", Boolean(file.image))

    return row
  }

  // ---- typing ----

  // Compose intent is hasContent() — the same emptiness rule the box uses — but a leftover
  // draft restored on connect is not a keystroke. typingEnabled stays off until connect
  // finishes so opening a thread with a stash does not light the teammate indicator.
  syncTyping() {
    if (!this.canPublishTyping()) return
    if (this.element.classList.contains("is-sending")) {
      this.stopTyping({ immediate: true })
      return
    }
    if (!this.hasContent()) {
      this.stopTyping({ immediate: true })
      return
    }

    this.pulseTyping()
  }

  canPublishTyping() {
    return this.typingEnabled && this.hasTypingUrlValue && this.typingUrlValue &&
           this.hasConversationIdValue && this.hasUserIdValue
  }

  pulseTyping() {
    cancelPendingTypingLeave(this.conversationIdValue, this.typingClientId)
    this.scheduleTypingIdle()
    if (this.typingHeartbeatAt && (Date.now() - this.typingHeartbeatAt) < TYPING_HEARTBEAT_MS) {
      return
    }

    this.heartbeatTyping()
  }

  scheduleTypingIdle() {
    window.clearTimeout(this.typingIdleTimer)
    this.typingIdleTimer = window.setTimeout(() => {
      this.stopTyping({ immediate: true })
    }, TYPING_IDLE_MS)
  }

  async heartbeatTyping() {
    this.typingHeartbeatAt = Date.now()
    this.typingPublished = true
    try {
      await fetch(this.typingUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken()
        },
        body: JSON.stringify({
          client_id: this.typingClientId,
          mode: this.element.dataset.mode || "reply"
        })
      })
    } catch {
      // The next keystroke retries; a dropped beat must not surface as a page error.
    }
  }

  stopTyping({ immediate = false, delay = TYPING_LEAVE_GRACE_MS } = {}) {
    window.clearTimeout(this.typingIdleTimer)
    this.typingIdleTimer = null
    this.typingHeartbeatAt = null
    if (!this.typingPublished || !this.hasTypingUrlValue) return

    const leave = {
      conversationId: this.conversationIdValue,
      clientId: this.typingClientId,
      url: this.typingUrlValue
    }

    if (immediate) {
      cancelPendingTypingLeave(leave.conversationId, leave.clientId)
      sendTypingLeave(leave)
      this.typingPublished = false
      return
    }

    this.typingPublished = false
    schedulePendingTypingLeave({ ...leave, delay })
  }

  typingVisibilityChanged() {
    if (document.visibilityState === "visible") return

    this.stopTyping({ immediate: true })
  }

  typingPageHide() {
    this.stopTyping({ immediate: true })
  }

  readTypingClientId() {
    if (!this.hasUserIdValue || !this.hasConversationIdValue) return window.crypto.randomUUID()

    const key = `${TYPING_CLIENT_PREFIX}.${this.userIdValue}.${this.conversationIdValue}`
    try {
      const existing = window.sessionStorage.getItem(key)
      if (existing) return existing

      const clientId = window.crypto.randomUUID()
      window.sessionStorage.setItem(key, clientId)
      return clientId
    } catch {
      return window.crypto.randomUUID()
    }
  }
}
